import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, CheckCircle2, Clock, MessageSquare, ChevronRight, Download, Activity, Zap, Star, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Helper: route AI calls through backend V2 endpoint (no keys in frontend)
async function callGlowyAI(messages, options = { narrative: true }) {
  try {
    const brandId = localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId');
    const authToken = localStorage.getItem('token');
    const isPlatformHost = (typeof window !== 'undefined') && /(^|\.)platform\.glowytics\.com$/i.test(window.location.hostname);
    const endpoint = isPlatformHost
      ? 'https://api.glowytics.com/api/ai/chat/v2/respond'
      : (process.env.NODE_ENV === 'development'
          ? '/api/ai/chat/v2/respond'
          : 'https://api.glowytics.com/api/ai/chat/v2/respond');

    const headers = {
      'Content-Type': 'application/json',
    };
    if (brandId) headers['X-Brand-ID'] = brandId;
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

    const resp = await fetch(endpoint, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ messages, options })
    });
    if (!resp.ok) {
      const errText = await resp.text();
      throw new Error(errText || `HTTP ${resp.status}`);
    }
    const data = await resp.json();
    return data?.assistantMessage || '';
  } catch (e) {
    console.error('GlowyAI call failed:', e);
    return '';
  }
}

// Enhanced Campaign Performance Data Analysis fonksiyonları
const analyzeUserQuery = (userMessage, conversationHistory = []) => {
  const message = userMessage.toLowerCase();
  
  // Campaign Performance Intent Detection
  const campaignKeywords = ['campaign', 'kampanya', 'performance', 'performans', 'roas', 'revenue', 'gelir', 'harcama', 'spend'];
  const timeKeywords = ['son', 'last', 'geçen', 'bu ay', 'this month', 'hafta', 'week', 'gün', 'day'];
  const platformKeywords = ['google ads', 'meta ads', 'facebook', 'instagram', 'karşılaştır', 'compare'];
  const channelKeywords = ['channel', 'kanal', 'kaynak', 'source', 'medium', 'genel performans'];
  const productKeywords = ['product', 'ürün', 'brand', 'marka', 'category', 'kategori'];
  // 🎯 COMPREHENSIVE SEMANTIC DICTIONARY
  const metricKeywords = {
    conversion_rate: {
      keywords: [
        // English - Formal
        'conversion rate', 'cvr', 'conversion ratio', 'convert rate',
        // English - Business/Casual
        'how many converted', 'conversion percentage', 'convert percentage',
        'how many bought', 'purchase rate', 'buying rate', 'conversion %',
        'what percentage bought', 'how many people bought', 'conversion stats',
        // Turkish - Formal
        'dönüşüm oranı', 'konversiyon oranı', 'çevirme oranı',
        // Turkish - Casual/Business
        'dönüşüm', 'konversiyon', 'çevirme', 'satışa dönüşüm',
        'kaç kişi aldı', 'kaça kaçı aldı', 'alım oranı'
      ],
      preferredSource: 'googleAds',
      fallback: ['metaAds']
    },
    sessions: {
      keywords: [
        // English - Formal
        'session', 'sessions', 'session count', 'traffic', 'visits', 'visitors',
        'unique visitors', 'site visits', 'website traffic', 'user sessions',
        // English - Business/Casual
        'how many visitors', 'how much traffic', 'visitor count', 'people visited',
        'website visitors', 'site traffic', 'web traffic', 'traffic volume',
        'how many people came', 'how many visits', 'visitor numbers',
        'people on site', 'site sessions', 'user count',
        // Turkish - Formal
        'oturum', 'oturum sayısı', 'trafik', 'ziyaretçi', 'ziyaret',
        // Turkish - Casual/Business
        'oturumlar', 'oturumum', 'oturumumuz', 'trafiğim', 'trafiğimiz',
        'kaç kişi geldi', 'kaç ziyaretçi', 'site trafiği', 'web trafiği',
        'ziyaret sayısı', 'giriş sayısı', 'kullanıcı sayısı'
      ],
      preferredSource: 'analytics',
      fallback: []
    },
    revenue: {
      keywords: [
        // English - Formal
        'revenue', 'sales', 'income', 'earnings', 'turnover', 'gross revenue',
        'total revenue', 'sales revenue', 'net revenue', 'sales income',
        // English - Business/Casual
        'how much did we make', 'how much did we sell', 'total sales',
        'money made', 'sales numbers', 'sales figures', 'sales amount',
        'how much revenue', 'revenue numbers', 'sales total', 'money earned',
        'our revenue', 'our sales', 'our earnings', 'total earned',
        'sales performance', 'revenue performance', 'what did we make',
        // Turkish - Formal
        'gelir', 'ciro', 'hasılat', 'gelir tutarı', 'satış tutarı',
        // Turkish - Business/Possessive
        'ciromuz', 'gelirimiz', 'hasılatımız', 'satışlarımız',
        'cirom', 'gelirim', 'hasılatım', 'satışlarım',
        // Turkish - Casual/Spoken
        'ne kadar sattık', 'ne kadar kazandık', 'toplam satış',
        'satış', 'satışlar', 'kazanç', 'kazancımız', 'para kazanımı',
        // Turkish - Total Revenue Specific
        'toplam gelir', 'bütün kanallar', 'tüm kanallar', 'genel gelir',
        'toplam ciro', 'genel performans', 'tüm channellar', 'bütün channellar',
        'total revenue from all channels', 'overall revenue', 'total earnings'
      ],
      preferredSource: 'analytics',
      fallback: ['googleAds', 'metaAds']
    },
    cost: {
      keywords: [
        // English - Formal
        'cost', 'spend', 'spending', 'budget', 'expenses', 'expenditure',
        'total cost', 'ad spend', 'advertising cost', 'campaign cost',
        'marketing spend', 'ad budget', 'total spend', 'cost analysis',
        // English - Business/Casual
        'how much did we spend', 'how much money spent', 'money spent',
        'our spending', 'our costs', 'our budget', 'total spending',
        'ad costs', 'advertising expenses', 'campaign spending', 'spent amount',
        'what did we spend', 'spending amount', 'cost amount', 'budget used',
        'how much spent', 'expenditure amount', 'money used',
        // Turkish - Formal
        'harcama', 'maliyet', 'bütçe', 'gider',
        // Turkish - Business/Possessive
        'harcamam', 'harcamamız', 'maliyetim', 'maliyetimiz',
        'bütçem', 'bütçemiz', 'giderim', 'giderimiz',
        // Turkish - Casual/Spoken
        'ne kadar harcadım', 'ne kadar harcadık', 'kaç para harcadık',
        'para harcaması', 'reklam harcaması', 'toplam harcama'
      ],
      preferredSource: 'googleAds',
      fallback: ['metaAds']
    },
    clicks: {
      keywords: [
        // English - Formal
        'clicks', 'click', 'click count', 'clickthrough', 'total clicks',
        'ad clicks', 'click volume', 'click numbers', 'click stats',
        // English - Business/Casual
        'how many clicks', 'how many people clicked', 'click amount',
        'people clicked', 'users clicked', 'our clicks', 'click performance',
        'clicks received', 'clicks generated', 'click traffic',
        'how many clicked', 'click data', 'clicking users',
        // Turkish - Formal
        'tıklama', 'tıklama sayısı', 'tık',
        // Turkish - Business/Possessive
        'tıklamalarım', 'tıklamalarımız', 'tıklarım', 'tıklarımız',
        // Turkish - Casual/Spoken
        'kaç kişi tıkladı', 'kaç tıklama', 'tıklama aldım',
        'reklamıma tıklama', 'tıklama sayım'
      ],
      preferredSource: 'googleAds',
      fallback: ['metaAds']
    },
    roas: {
      keywords: [
        // English - Formal
        'roas', 'return on ad spend', 'roi', 'return on investment',
        'ad return', 'advertising roi', 'campaign roi', 'marketing roi',
        'return ratio', 'profitability', 'efficiency ratio',
        // English - Business/Casual
        'how profitable', 'is it profitable', 'return value', 'efficiency',
        'money back', 'return per dollar', 'value for money', 'worth it',
        'profitable campaigns', 'campaign efficiency', 'ad efficiency',
        'how effective', 'performance ratio', 'return rate',
        // Turkish - Formal
        'roas', 'reklam yatırım getirisi', 'yatırım getirisi',
        // Turkish - Business
        'getiri', 'geri dönüş', 'verimlilik',
        // Turkish - Casual/Spoken
        'ne kadar verimli', 'karlı mı', 'getirisi nasıl',
        'para karşılığı', 'harcadığımın karşılığı'
      ],
      preferredSource: 'googleAds',
      fallback: ['metaAds', 'analytics']
    },
    impressions: {
      keywords: [
        // English - Formal
        'impressions', 'impression', 'imp', 'views', 'reach', 'ad views',
        'total impressions', 'impression count', 'view count', 'ad impressions',
        'display count', 'show count', 'exposure count',
        // English - Business/Casual
        'how many saw', 'how many views', 'people saw', 'users saw',
        'how many people saw', 'times shown', 'ad shows', 'visibility',
        'people reached', 'audience reached', 'views received',
        'times displayed', 'how often shown', 'exposure numbers',
        // Turkish - Formal
        'gösterim', 'gösterim sayısı', 'izlenim', 'erişim',
        // Turkish - Business/Possessive
        'gösterimlerim', 'gösterimlerimiz', 'izlenimlerim',
        // Turkish - Casual/Spoken
        'kaç kişi gördü', 'kaç gösterim', 'kaça gösterildi',
        'reklam gösterimi', 'kaç kişiye ulaştı'
      ],
      preferredSource: 'googleAds',
      fallback: ['metaAds']
    },
    cpc: {
      keywords: [
        // English - Formal
        'cpc', 'cost per click', 'click cost', 'cost-per-click',
        'average cpc', 'click price', 'per click cost', 'click rate',
        // English - Business/Casual
        'how much per click', 'cost of each click', 'price per click',
        'what do we pay per click', 'click pricing', 'cost for clicks',
        'how much for a click', 'click costs', 'per click price',
        'cost of clicking', 'click expenses', 'price of clicks',
        // Turkish - Formal
        'tıklama başına maliyet', 'tıklama maliyeti',
        // Turkish - Business
        'tık maliyeti', 'tıklama fiyatı',
        // Turkish - Casual/Spoken
        'bir tıklama kaç para', 'tıklama ne kadar',
        'tıklamaya ne ödüyorum'
      ],
      preferredSource: 'googleAds',
      fallback: ['metaAds']
    }
  };
  const comparisonKeywords = ['compare', 'karşılaştır', 'daha iyi', 'better', 'vs'];
  // Paid vs Non-Paid detection keywords
  const paidKeywords = ['paid', 'ücretli', 'reklamlı', 'cpc', 'ppc', 'paid search', 'ad', 'ads'];
  const nonPaidKeywords = ['non paid', 'non-paid', 'nonpaid', 'organik', 'organic', 'ücretsiz', 'direct', 'referral', 'email'];
  
  // 📊 COMPREHENSIVE ANALYSIS PATTERNS SEMANTIC DICTIONARY
  const analysisPatterns = {
    monthly_breakdown: /aylık\s+kırılım|monthly\s+breakdown|aylık.*kırılım|kırılımda\s+görmek|aylık.*görmek|aylık.*istiyorum|ay\s+ay|ayların|aylara\s+böl|aylık\s+detay|aylık\s+analiz|aylık\s+performans|aylık\s+dağılım|month\s+by\s+month|monthly\s+data|monthly\s+analysis|by\s+month|each\s+month|monthly\s+view|monthly\s+report/i,
    
    daily_breakdown: /günlük\s+kırılım|daily\s+breakdown|günlük.*detay|gün\s+gün|günlerin|günlere\s+böl|günlük\s+detay|günlük\s+analiz|günlük\s+performans|her\s+gün|day\s+by\s+day|daily\s+data|daily\s+analysis|by\s+day|each\s+day|daily\s+view|daily\s+report|per\s+day/i,
    
    campaign_listing: /kampanya.*liste|campaign.*list|hangi\s+kampanya|kampanyalar.*göster|kampanya\s+isimleri|kampanya\s+adları|kampanyalarım|aktif\s+kampanya|çalışan\s+kampanya|kampanya\s+listesi|kampanyaları\s+göster|kampanyalarımızı.*liste|listeler.*misin|listele.*misin|which\s+campaigns|show\s+campaigns|campaign\s+names|list\s+campaigns|our\s+campaigns|active\s+campaigns|running\s+campaigns|all\s+campaigns|should.*optimize|optimize.*campaign|kampanya.*optimize|optimizasyon.*gereken|hangi.*optimiz|optimize.*should|öneriler.*kampanya|kampanya.*öneriler|optimize.*edilmesi.*gereken|campaign.*optimization.*recommendation|optimization.*recommendation/i,
    
    trend_analysis: /trend|trendi|değişim|change|artış|azalış|yükseliş|düşüş|gelişim|seyir|gidişat|performans\s+seyri|nasıl\s+gidiyor|artıyor\s+mu|azalıyor\s+mu|iyileşiyor|kötüleşiyor|trending|trends|going\s+up|going\s+down|increasing|decreasing|improving|getting\s+worse|how\s+is\s+it\s+going|performance\s+trend|growth|decline/i,
    
    comparison: /karşılaştır|compare|vs|ile\s+karşı|hangisi\s+daha|karşılaştırma|hangi\s+platform|platform.*karşı|google.*meta|meta.*google|daha\s+iyi|daha\s+kötü|fark\s+nedir|versus|compared\s+to|which\s+is\s+better|which\s+performs\s+better|platform\s+comparison|difference\s+between|better\s+than|worse\s+than|comparison/i,
    
    summary: /özet|summary|genel|overall|toplam.*performans|genel\s+durum|nasıl\s+gidiyor|durumumuz|performansımız|genel\s+bakış|toplam\s+durum|özetle|özetini|overview|general\s+view|how\s+are\s+we\s+doing|performance\s+summary|total\s+performance|general\s+performance|summarize|give\s+me\s+an\s+overview/i
  };
  
  // 🎯 ROAS Trend Analysis Keywords - 5+4 Day Analysis
  const roasTrendKeywords = [
    'bütçe optimizasyonu', 'budget optimization', 'bütçe artır', 'bütçe azalt', 
    'roas trend', 'roas trendi', '5+4', '5 4', 'ilk 5 gün', 'son 4 gün',
    'budget recommendation', 'bütçe önerisi', 'kampanya optimizasyonu',
    'spend optimization', 'harcama optimizasyonu', '9 günlük analiz',
    'performance trend', 'performans trendi', 'trend analizi'
  ];

  // Budget optimization suggestions keywords (explicit ask for recommendations)
  const budgetOptimizationSuggestionKeywords = [
    'bütçe önerilerin', 'bütçe önerisi', 'ne önerirsin', 'önerilerin ne', 'ne olur önerin',
    'budget suggestions', 'budget recommendation', 'what should we do with budget',
    'artırmalı mıyım', 'azaltmalı mıyım', 'budget optimize', 'optimize budget'
  ];

  // 🎯 Budget Reallocation - 7+2 Method
  const budgetReallocationKeywords = [
    '7+2', '7 + 2', '7plus2', 'seven plus two',
    'budget reallocation', 'budget re-alloc', 'spend reallocation',
    'bütçe yeniden dağıtımı', 'bütçe realloc', 'bütçe re-allocation',
    'bütçe dağılımı', 'harcama yeniden dağıtımı'
  ];
  
  // Campaign listing keywords - NEW
  const campaignListKeywords = ['listele', 'göster', 'listeler', 'görmek', 'liste', 'isimler', 'isimleri', 'names', 'list', 'show me', 'kampanyalar', 'campaigns', 'misin', 'listeler misin', 'listele misin'];
  
  // Detailed analysis keywords
  const detailedKeywords = ['detay', 'detail', 'ayrıntı', 'breakdown', 'kırılım', 'daily', 'günlük', 'hangi kampanya', 'which campaign', 'kampanya bazında', 'campaign wise'];
  const campaignTypeKeywords = ['search', 'shopping', 'display', 'performance max', 'pmax', 'video', 'youtube'];
  const topCampaignsKeywords = ['en iyi', 'best', 'top', 'en başarılı'];
  const thresholdExprRegex = /(roas|harcama|cost|spend)\s*[<>]=?\s*[\d.,]+/i;
  const compareKeywords = ['karşılaştır', 'compare', 'vs', 'daha iyi mi', 'hangisi daha iyi'];
  const anomalyKeywords = ['anomali', 'anomaly', 'ani düşüş', 'ani artış', 'spike', 'dip'];
  const thresholdKeywords = ['roas', 'cpc', 'cpa', 'harcama', 'cost', 'spend'];
  const metricDefinitionKeywords = ['nedir', 'nedir?', 'what is', 'nasıl hesaplanır', 'formülü'];
  
  // Month mapping - Turkish to English
  const monthMap = {
    'ocak': 1, 'january': 1,
    'şubat': 2, 'february': 2,
    'mart': 3, 'march': 3,
    'nisan': 4, 'april': 4,
    'mayıs': 5, 'may': 5,
    'haziran': 6, 'june': 6,
    'temmuz': 7, 'july': 7,
    'ağustos': 8, 'august': 8,
    'eylül': 9, 'september': 9,
    'ekim': 10, 'october': 10,
    'kasım': 11, 'november': 11,
    'aralık': 12, 'december': 12
  };
  
  const intent = {
    type: null,
    timeframe: null,
    customDateRange: null,
    platform: 'all',
    confidence: 0,
    groupBy: 'date',
    campaignName: null,
    campaignType: null,
    analysisType: null,
    metric: null,
    brandName: null,
    year: null,
    channel: null,
    isComparison: false,
    // Enhanced fields
    dataSource: null,
    preferredSource: null,
    analysisPattern: null,
    clarificationNeeded: false,
    suggestedQuestions: [],
    contextHints: {}
  };
  
  // Year detection
  const yearMatch = message.match(/(\d{4})\s*(yılında|yılı|)/);
  if (yearMatch) {
    intent.year = parseInt(yearMatch[1]);
  }

  // Comparison detection
  if (comparisonKeywords.some(kw => message.includes(kw))) {
    intent.isComparison = true;
  }

  let timeframeDetected = false;
  
  // Geçen ay algılama (en öncelikli)
  if (message.includes('geçen ay') || message.includes('last month') || message.includes('önceki ay')) {
    const now = new Date();
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const year = lastMonthDate.getFullYear();
    const month = lastMonthDate.getMonth() + 1;
    
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];

    intent.customDateRange = {
      start: startDateStr,
      end: endDateStr,
      type: 'month',
      monthNumber: month
    };
    intent.timeframe = 'custom';
    intent.confidence = 1.0; // En yüksek öncelik
    timeframeDetected = true; // Diğer kuralları atla
    console.log(`🗓️ Prioritized "geçen ay": ${startDateStr} to ${endDateStr}`);
  }

  // Yıllık aylık kırılım algılama (2025 revenue aylık kırılım gibi) - only if user explicitly wants monthly breakdown
  const yearlyBreakdownMatch = message.match(/(\d{4}).*?(aylık\s+kırılım|monthly\s+breakdown)/i);
  if (yearlyBreakdownMatch && !timeframeDetected) {
    const year = parseInt(yearlyBreakdownMatch[1]);
    intent.customDateRange = {
      type: 'yearly_monthly_breakdown',
      year: year
    };
    intent.timeframe = 'yearly_monthly';
    intent.confidence += 0.5;
    timeframeDetected = true;
    console.log(`🗓️ Detected yearly monthly breakdown: ${year}`);
  }

  // Paid vs Non-Paid intent (detect early to avoid generic campaign template)
  if (!intent.type) {
    const mentionsPaid = paidKeywords.some(kw => message.includes(kw));
    const mentionsNonPaid = nonPaidKeywords.some(kw => message.includes(kw));
    const mentionsSplit = /(dağılım|kırılım|split|böl|vs|karşılaştır)/i.test(message);
    if ((mentionsPaid && mentionsNonPaid) || (mentionsPaid && /non/i.test(message)) || (mentionsNonPaid && /paid/i.test(message))) {
      intent.type = 'paid_nonpaid';
      intent.confidence = 0.9;
    }
  }

  // 🗓️ COMPREHENSIVE TIMEFRAME SEMANTIC DICTIONARY
  const timeframePatterns = [
    // 📅 MONTHS - Aylık Patterns (Turkish & English)
    { regex: /(\d+)\s*aylık/i, type: 'months' },
    { regex: /(\d+)\s*aydır/i, type: 'months' },
    { regex: /(\d+)\s*ayda/i, type: 'months' },
    { regex: /son\s+(\d+)\s+ay/i, type: 'months' },
    { regex: /geçen\s+(\d+)\s+ay/i, type: 'months' },
    { regex: /(\d+)\s+ay\b/i, type: 'months' },
    { regex: /(\d+)\s+month/i, type: 'months' },
    { regex: /(\d+)\s+months/i, type: 'months' },
    { regex: /last\s+(\d+)\s+months/i, type: 'months' },
    { regex: /past\s+(\d+)\s+months/i, type: 'months' },
    { regex: /(\d+)\s+month\s+period/i, type: 'months' },
    { regex: /(\d+)\s+aylık\s+süre/i, type: 'months' },
    { regex: /(\d+)\s+aylık\s+dönem/i, type: 'months' },
    { regex: /(\d+)\s+aylık\s+periyot/i, type: 'months' },
    
    // 📆 WEEKS - Haftalık Patterns (Turkish & English)
    { regex: /(\d+)\s*haftalık/i, type: 'weeks' },
    { regex: /(\d+)\s*haftadır/i, type: 'weeks' },
    { regex: /(\d+)\s*haftada/i, type: 'weeks' },
    { regex: /son\s+(\d+)\s+hafta/i, type: 'weeks' },
    { regex: /geçen\s+(\d+)\s+hafta/i, type: 'weeks' },
    { regex: /(\d+)\s+hafta\b/i, type: 'weeks' },
    { regex: /(\d+)\s+week/i, type: 'weeks' },
    { regex: /(\d+)\s+weeks/i, type: 'weeks' },
    { regex: /last\s+(\d+)\s+weeks/i, type: 'weeks' },
    { regex: /past\s+(\d+)\s+weeks/i, type: 'weeks' },
    { regex: /(\d+)\s+week\s+period/i, type: 'weeks' },
    { regex: /(\d+)\s+haftalık\s+süre/i, type: 'weeks' },
    
    // 📋 DAYS - Günlük Patterns (Turkish & English)
    { regex: /(\d+)\s*günlük/i, type: 'days' },
    { regex: /(\d+)\s*gündür/i, type: 'days' },
    { regex: /(\d+)\s*günde/i, type: 'days' },
    { regex: /son\s+(\d+)\s+gün/i, type: 'days' },
    { regex: /geçen\s+(\d+)\s+gün/i, type: 'days' },
    { regex: /(\d+)\s+gün\b/i, type: 'days' },
    { regex: /(\d+)\s+day/i, type: 'days' },
    { regex: /(\d+)\s+days/i, type: 'days' },
    { regex: /last\s+(\d+)\s+days/i, type: 'days' },
    { regex: /past\s+(\d+)\s+days/i, type: 'days' },
    { regex: /(\d+)\s+day\s+period/i, type: 'days' },
    { regex: /(\d+)\s+günlük\s+süre/i, type: 'days' },
    
    // 🎯 SPECIFIC TIME PERIODS - Özel Dönemler (Turkish & English)
    { regex: /yarım\s+yıl|6\s+ay|altı\s+ay|half\s+year|six\s+months/i, type: 'specific', value: 180 },
    { regex: /çeyrek\s+yıl|3\s+ay|üç\s+ay|quarter\s+year|three\s+months|quarterly/i, type: 'specific', value: 90 },
    { regex: /iki\s+ay|2\s+ay|two\s+months/i, type: 'specific', value: 60 },
    { regex: /bir\s+ay|1\s+ay|one\s+month|a\s+month/i, type: 'specific', value: 30 },
    { regex: /this\s+month|bu\s+ay/i, type: 'specific', value: 30 },
    { regex: /iki\s+hafta|2\s+hafta|two\s+weeks|fortnight/i, type: 'specific', value: 14 },
    { regex: /last\s+week|geçen\s+hafta|son\s+hafta|bir\s+hafta|1\s+hafta|one\s+week|a\s+week/i, type: 'specific', value: 7 }
  ];
  
  if (!timeframeDetected) {
    for (const pattern of timeframePatterns) {
      const match = message.match(pattern.regex);
      if (match) {
        if (pattern.type === 'specific') {
          intent.timeframe = pattern.value.toString();
          console.log(`🗓️ Detected specific period -> ${intent.timeframe} days`);
        } else {
          const value = parseInt(match[1]);
          if (pattern.type === 'months') {
            intent.timeframe = (value * 30).toString();
            console.log(`🗓️ Detected ${value} months -> ${intent.timeframe} days`);
          } else if (pattern.type === 'weeks') {
            intent.timeframe = (value * 7).toString();
            console.log(`🗓️ Detected ${value} weeks -> ${intent.timeframe} days`);
          } else if (pattern.type === 'days') {
            intent.timeframe = value.toString();
            console.log(`🗓️ Detected ${value} days`);
          }
        }
        intent.confidence += 0.3;
        timeframeDetected = true;
        break;
      }
    }
  }
  
  // Default timeframe if nothing detected
  if (!timeframeDetected && !intent.customDateRange && !intent.timeframe) {
    // Aylık kelimesi geçiyorsa daha uzun default timeframe
    if (message.includes('aylık') || message.includes('monthly')) {
      intent.timeframe = '365'; // 12 aylık veri için
      console.log(`🗓️ Using extended timeframe for monthly request: 365 days`);
    } else {
      intent.timeframe = '30'; // default 30 days
      console.log(`🗓️ Using default timeframe: 30 days`);
    }
  }

  // UNIFIED CHANNEL PERFORMANCE INTENT
  const specificChannelKeywords = ['direct', 'organic', 'referral', 'cpc', 'paid search', 'social', 'google ads', 'meta ads'];
  const detectedChannels = specificChannelKeywords.filter(ch => new RegExp(`\\b${ch}\\b`, 'i').test(message));

  if (
    intent.type !== 'paid_nonpaid' &&
    !(topCampaignsKeywords && topCampaignsKeywords.some(k => message.includes(k))) &&
    (channelKeywords.some(kw => message.includes(kw)) || detectedChannels.length > 0)
  ) {
    intent.type = 'channel_performance';
    intent.confidence = 0.9;
    
    if (detectedChannels.length > 0) {
      intent.channel = detectedChannels[0]; // Take the first detected channel
    }

    if (!intent.timeframe) {
      intent.timeframe = '120';
      console.log('🗓️ No timeframe specified for channel query, defaulting to 120 days.');
    }
  }

  // Product Performance Intent Detection
  else if (productKeywords.some(keyword => message.includes(keyword))) {
    intent.type = 'product_performance';
    intent.confidence += 0.8;
    if (message.includes('brand') || message.includes('marka')) {
      intent.analysisType = 'brands';
      const brandMatch = message.match(/([\w\s.&'-]+) (markasının|markası|brand)/i);
      if (brandMatch && brandMatch[1].toLowerCase().trim() !== 'hangi') {
        intent.brandName = brandMatch[1].trim();
      }
    } else if (message.includes('category') || message.includes('kategori')) {
      intent.analysisType = 'categories';
    } else {
      intent.analysisType = 'products';
    }
    
    // NEW: Detect specific metric
    for (const [metric, keywords] of Object.entries(metricKeywords)) {
        if (keywords.some(kw => message.includes(kw))) {
            intent.metric = metric;
            intent.confidence += 0.1; // boost confidence
            break;
        }
    }
  }
  // Total Revenue Intent Detection (high priority)
  else if (metricKeywords.revenue.keywords.some(keyword => message.includes(keyword)) && 
           (message.includes('toplam') || message.includes('total') || message.includes('bütün') || message.includes('tüm') || message.includes('genel'))) {
    intent.type = 'total_revenue';
    intent.confidence += 0.9;
    console.log('🎯 Detected total revenue query - will fetch all channels data');
  }
  // Channel Revenue Intent Detection
  else if (channelKeywords.some(keyword => message.includes(keyword)) && campaignKeywords.some(keyword => message.includes(keyword))) {
    intent.type = 'channel_monthly_revenue';
    intent.confidence += 0.8;
  }
  // Campaign Performance Detection
  else if (
    intent.type !== 'paid_nonpaid' &&
    !(topCampaignsKeywords && topCampaignsKeywords.some(k => message.includes(k))) &&
    !thresholdExprRegex.test(message) &&
    campaignKeywords.some(keyword => message.includes(keyword))
  ) {
    intent.type = 'campaign_performance';
    intent.confidence += 0.6;
    
    // Check if user wants to list/see campaign names - PRIORITY CHECK
    if (campaignListKeywords.some(keyword => message.includes(keyword))) {
      intent.groupBy = 'campaign_name';
      intent.confidence += 0.4;
      console.log('🎯 Detected campaign listing request - using campaign_name groupBy');
    }
    // Determine grouping strategy for detailed view
    else if (message.includes('günlük') || message.includes('daily') || message.includes('tarih') || message.includes('date')) {
      intent.groupBy = 'date';
    } else if (message.includes('kampanya bazında') || message.includes('campaign wise') || message.includes('hangi kampanya') || message.includes('which campaign')) {
      intent.groupBy = 'campaign_name';
    } else if (message.includes('campaign type') || message.includes('kampanya türü') || campaignTypeKeywords.some(keyword => message.includes(keyword))) {
      intent.groupBy = 'campaign_type';
    } else if (detailedKeywords.some(keyword => message.includes(keyword))) {
      intent.groupBy = 'date_campaign'; // Most detailed view
    }
    
    // Platform Detection
    if (message.includes('google ads') || message.includes('google')) {
      intent.platform = 'google_ads';
      intent.confidence += 0.2;
    } else if (message.includes('meta') || message.includes('facebook') || message.includes('instagram')) {
      intent.platform = 'meta_ads';
      intent.confidence += 0.2;
    } else if (message.includes('karşılaştır') || message.includes('compare')) {
      intent.platform = 'all';
      intent.confidence += 0.3;
    }
    
    // Default to 'all' platforms for campaign listing when no specific platform is mentioned
    if (!intent.platform && intent.groupBy === 'campaign_name') {
      intent.platform = 'all';
      intent.confidence += 0.1;
      console.log('🎯 No specific platform mentioned for campaign listing - defaulting to all platforms');
    }
    
    // Campaign Type Detection for Google Ads
    if (intent.platform === 'google_ads' || intent.platform === 'all') {
      if (message.includes('search')) {
        intent.campaignType = 'SEARCH';
      } else if (message.includes('shopping')) {
        intent.campaignType = 'SHOPPING';
      } else if (message.includes('display')) {
        intent.campaignType = 'DISPLAY';
      } else if (message.includes('performance max') || message.includes('pmax')) {
        intent.campaignType = 'PERFORMANCE_MAX';
      } else if (message.includes('video') || message.includes('youtube')) {
        intent.campaignType = 'VIDEO';
      }
    }
    
    // Campaign Name Detection (look for specific campaign names)
    const campaignNameMatch = message.match(/kampanya[:\s]+([^,.\s]+)|campaign[:\s]+([^,.\s]+)/i);
    if (campaignNameMatch) {
      intent.campaignName = campaignNameMatch[1] || campaignNameMatch[2];
    }
    
  // Specific Month Detection (applies to all intents) - require explicit monthly/report keywords to avoid over-triggering
    let monthDetected = false;
    for (const [monthName, monthNumber] of Object.entries(monthMap)) {
      if (message.includes(monthName) && /(aylık|monthly|rapor|kırılım|breakdown)/i.test(message)) {
        const year = new Date().getFullYear(); // Use current year
        const lastDay = new Date(year, monthNumber, 0).getDate();
        
        const startDateStr = `${year}-${String(monthNumber).padStart(2, '0')}-01`;
        const endDateStr = `${year}-${String(monthNumber).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        
        intent.customDateRange = {
          start: startDateStr,
          end: endDateStr,
          type: 'month',
          monthName: monthName,
          monthNumber: monthNumber
        };
        intent.timeframe = 'custom';
        intent.confidence += 0.3;
        monthDetected = true;
        
        console.log(`🗓️ Month detected: ${monthName} (${monthNumber}) -> ${startDateStr} to ${endDateStr}`);
        break;
      }
    }
    
    // Legacy timeframe detection (backup)
    if (!monthDetected && !timeframeDetected) {
      if (message.includes('hafta') || message.includes('week')) {
        intent.timeframe = '7';
        console.log(`🗓️ Legacy detection: week -> 7 days`);
      } else if (message.includes('üç ay') || message.includes('quarter')) {
        intent.timeframe = '90';
        console.log(`🗓️ Legacy detection: quarter -> 90 days`);
      }
    }
  }

  // Top N campaigns request (default 30)
  if (!intent.type && (topCampaignsKeywords.some(k => message.includes(k)) && /(kampanya|campaign)/i.test(message))) {
    intent.type = 'top_campaigns';
    const m = message.match(/(top|en iyi)\s*(\d{1,3})/i);
    intent.topN = m ? Math.min(parseInt(m[2]), 100) : 30;
    if (message.includes('google ads') || /\bgoogle\b/i.test(message)) {
      intent.platform = 'google_ads';
    } else if (message.includes('meta') || message.includes('facebook') || message.includes('instagram')) {
      intent.platform = 'meta_ads';
    } else {
      intent.platform = 'all';
    }
    intent.groupBy = 'campaign_name';
    if (!intent.timeframe) intent.timeframe = '30';
    intent.confidence = Math.max(intent.confidence, 0.9);
    console.log('🎯 Intent: top_campaigns', intent.platform, intent.topN);
  }
  
  // 🎯 ROAS Trend Analysis Intent Detection (5+4 Day Analysis)
  if (roasTrendKeywords.some(keyword => message.includes(keyword))) {
    intent.type = 'roas_trend_analysis';
    intent.timeframe = '9'; // Fixed 9 days for 5+4 analysis
    intent.confidence = 0.95;
    console.log('🎯 ROAS Trend Analysis intent detected - 5+4 day methodology');
  }

  // Budget optimization suggestions (5+4 rule with direct increase/decrease)
  if (!intent.type && budgetOptimizationSuggestionKeywords.some(k => message.includes(k))) {
    intent.type = 'budget_optimization_suggestions';
    intent.timeframe = '9';
    intent.confidence = 0.9;
    console.log('🎯 Budget Optimization Suggestions intent detected (5+4 rule, direct rec)');
  }
  // 7+2 budget reallocation detection
  if (!intent.type && budgetReallocationKeywords.some(k => message.includes(k))) {
    intent.type = 'budget_reallocation_7plus2';
    intent.timeframe = '9';
    intent.groupBy = 'date_campaign';
    intent.platform = 'google_ads'; // daily detailed available
    intent.confidence = 0.95;
    console.log('🎯 Budget Reallocation intent detected - 7+2 day methodology');
  }
  
  // Enhanced metric detection with data source intelligence
  for (const [metricType, metricConfig] of Object.entries(metricKeywords)) {
    if (metricConfig.keywords.some(keyword => message.includes(keyword))) {
      intent.metric = metricType;
      intent.preferredSource = metricConfig.preferredSource;
      intent.confidence += 0.2;
      console.log(`🎯 Metric detected: ${metricType}, preferred source: ${metricConfig.preferredSource}`);
      
      // Data source routing based on metric
      if (!intent.type) {
        if (metricConfig.preferredSource === 'analytics') {
          intent.type = 'channel_performance';
          intent.dataSource = 'analytics';
        } else if (metricConfig.preferredSource === 'googleAds') {
          intent.type = 'campaign_performance';
          intent.platform = 'google_ads';
          intent.dataSource = 'googleAds';
        } else if (metricConfig.preferredSource === 'metaAds') {
          intent.type = 'campaign_performance';
          intent.platform = 'meta_ads';
          intent.dataSource = 'metaAds';
        }
        intent.confidence = 0.85; // Boost confidence for metric-based routing
        console.log(`🎯 ${metricType} query routed to ${intent.type} with ${intent.dataSource}`);
      }
      break;
    }
  }
  
  // Compare Google vs Meta (simple channel compare)
  if (!intent.type && (compareKeywords.some(k => message.includes(k)) || /(google).*?(meta)|(meta).*?(google)/i.test(message))) {
    intent.type = 'compare_google_meta';
    if (!intent.timeframe) intent.timeframe = '30';
    intent.confidence = Math.max(intent.confidence, 0.9);
    console.log('🎯 Intent: compare_google_meta');
  }

  // Anomaly overview (daily trend last 7 vs previous 7)
  if (!intent.type && anomalyKeywords.some(k => message.includes(k))) {
    intent.type = 'anomaly_overview';
    if (!intent.timeframe) intent.timeframe = '30';
    intent.confidence = Math.max(intent.confidence, 0.85);
    console.log('🎯 Intent: anomaly_overview');
  }

  // Threshold filter (e.g., ROAS < 2 and spend > 1000)
  if (!intent.type && thresholdExprRegex.test(message)) {
    intent.type = 'filter_campaigns_by_thresholds';
    if (!intent.timeframe) intent.timeframe = '30';
    intent.platform = /meta|facebook|instagram/i.test(message) ? 'meta_ads' : /google/i.test(message) ? 'google_ads' : 'google_ads';
    intent.confidence = Math.max(intent.confidence, 0.85);
    console.log('🎯 Intent: filter_campaigns_by_thresholds');
  }

  // Metric definition (what is ROAS/CR/CPA?)
  if (!intent.type && metricDefinitionKeywords.some(k => message.includes(k))) {
    const metricMap = {
      roas: 'ROAS (Return on Ad Spend) = Gelir / Harcama. Örnek: ₺10.000 gelir, ₺2.000 harcama → ROAS 5.00',
      cr: 'CR (Conversion Rate) = Dönüşüm / Oturum. Örnek: 100 satın alma / 5.000 oturum → %2.0',
      cpa: 'CPA (Cost per Acquisition) = Harcama / Dönüşüm. Örnek: ₺2.000 harcama / 100 satın alma → ₺20',
      cpc: 'CPC (Cost per Click) = Harcama / Tıklama. Örnek: ₺1.000 / 500 tıklama → ₺2,00'
    };
    const key = Object.keys(metricMap).find(k => message.includes(k));
    if (key) {
      intent.type = 'metric_definition';
      intent.metric = key;
      intent.definitionText = metricMap[key];
      intent.confidence = 1.0;
      console.log('🎯 Intent: metric_definition', key);
    }
  }
  
  // Analytics monthly revenue breakdown (e.g., "son 6 aylık ciro" → month-by-month)
  try {
    const asksMonthly = /(aylık|monthly|ay\s+ay|month\s+by\s+month|monthly\s+breakdown)/i.test(message);
    const mentionsRevenue = /(ciro|gelir|revenue|sales)/i.test(message);
    const monthsQuery = message.match(/(son|last)\s*(\d+)\s*(ay|months)/i);
    if (mentionsRevenue && (asksMonthly || monthsQuery)) {
      intent.type = 'analytics_monthly_revenue_breakdown';
      if (monthsQuery) {
        const num = parseInt(monthsQuery[2]);
        if (!isNaN(num) && num > 0) intent.timeframe = String(num * 30);
      }
      if (!intent.timeframe) intent.timeframe = '180';
      intent.groupBy = 'month';
      intent.confidence = Math.max(intent.confidence, 0.9);
      console.log('🎯 Intent: analytics_monthly_revenue_breakdown');
    }
  } catch (_) {}

  // Analytics monthly organic performance (sessions + revenue) e.g., "son 6 aylık organic performans"
  try {
    const mentionsOrganic = /(organik|organic)/i.test(message);
    const asksMonthly2 = /(aylık|monthly|ay\s+ay|month\s+by\s+month|monthly\s+breakdown|performans)/i.test(message);
    const monthsQuery2 = message.match(/(son|last)\s*(\d+)\s*(ay|months)/i);
    if (mentionsOrganic && (asksMonthly2 || monthsQuery2)) {
      intent.type = 'analytics_monthly_organic_breakdown';
      if (monthsQuery2) {
        const num = parseInt(monthsQuery2[2]);
        if (!isNaN(num) && num > 0) intent.timeframe = String(num * 30);
      }
      if (!intent.timeframe) intent.timeframe = '180';
      intent.groupBy = 'month';
      intent.confidence = Math.max(intent.confidence, 0.92);
      console.log('🎯 Intent: analytics_monthly_organic_breakdown');
    }
  } catch (_) {}
  
  // Analysis pattern detection
  for (const [patternType, regex] of Object.entries(analysisPatterns)) {
    if (regex.test(message)) {
      // If user asks for paid vs non-paid, don't force monthly/daily campaign listing patterns
      if (intent.type === 'paid_nonpaid') {
        break;
      }
      intent.analysisPattern = patternType;
      intent.confidence += 0.3;
      console.log(`🎯 Analysis pattern detected: ${patternType}`);
      
      // Adjust groupBy based on pattern
      if (patternType === 'monthly_breakdown') {
        intent.groupBy = 'month';
        // Aylık kırılım istekleri için daha uzun timeframe
        if (!intent.timeframe || parseInt(intent.timeframe) < 180) {
          intent.timeframe = '365'; // 12 aylık veri için
          console.log(`🗓️ Setting timeframe to 365 days for monthly breakdown`);
        }
        console.log(`🗓️ Setting groupBy to 'month' for monthly breakdown`);
      } else if (patternType === 'daily_breakdown') {
        intent.groupBy = 'date';
      } else if (patternType === 'campaign_listing') {
        intent.groupBy = 'campaign_name';
      }
      break;
    }
  }
  
  return intent;
};

// Logo placeholders'ı gerçek logolarla değiştiren fonksiyon
const processMessageContent = (content) => {
  if (!content) return content;
  
  let processedContent = content;
  
  // Logo placeholder'larını gerçek img etiketleriyle değiştir
  processedContent = processedContent.replace(
    /\[GOOGLE_ADS_LOGO\]/g, 
    '<img src="/google_ads-512.webp" width="16" height="16" style="display: inline; vertical-align: middle; margin-right: 4px;" alt="Google Ads" />'
  );
  
  processedContent = processedContent.replace(
    /\[META_ADS_LOGO\]/g, 
    '<img src="/6033716.png" width="16" height="16" style="display: inline; vertical-align: middle; margin-right: 4px;" alt="Meta Ads" />'
  );
  
  processedContent = processedContent.replace(
    /\[GA4_LOGO\]/g, 
    '<img src="/GA4logoicon.svg" width="16" height="16" style="display: inline; vertical-align: middle; margin-right: 4px;" alt="Google Analytics" />'
  );
  
  // Markdown formatlarını HTML'e çevir
  processedContent = processedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  processedContent = processedContent.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Line breaks'i HTML br'e çevir
  processedContent = processedContent.replace(/\n/g, '<br>');
  
  return processedContent;
};

const fetchCampaignData = async (intent) => {
  try {
    // Brand ID'yi al
    const brandId = localStorage.getItem('selectedBrandId') || 
                   localStorage.getItem('currentBrandId') ||
                   sessionStorage.getItem('selectedBrandId') ||
                   'f7e99143-5e9c-4bc8-9052-632613745bb2';
    
    console.log('🔍 Fetching campaign data with intent:', intent);
    console.log('🏢 Using brand ID:', brandId);
    
    const params = new URLSearchParams({
      platform: intent.platform,
      metrics: 'basic'
    });
    
    // Handle custom date range or standard timeframe
    if (intent.timeframe === 'custom' && intent.customDateRange) {
      params.append('startDate', intent.customDateRange.start);
      params.append('endDate', intent.customDateRange.end);
      console.log(`📅 Using custom date range: ${intent.customDateRange.start} to ${intent.customDateRange.end}`);
    } else {
      params.append('dateRange', intent.timeframe || '30');
      console.log(`📅 Using standard date range: ${intent.timeframe || '30'} days`);
    }
    
    const response = await axios.get(`https://api.glowytics.com/api/ai/campaign-performance/${brandId}?${params}`, {
      headers: {
        'X-Brand-ID': brandId
      },
      withCredentials: true
    });
    
    console.log('✅ Campaign data fetched successfully:', response.data);
    
    // Debug Meta Ads campaign names (can be removed in production)
    if (response.data.data && response.data.data.meta_ads && response.data.data.meta_ads.length > 0) {
      console.log('🔍 Meta Ads sample campaigns:', response.data.data.meta_ads.slice(0, 2).map(c => ({
        campaign_name: c.campaign_name,
        campaign_id: c.campaign_id,
        name: c.name,
        title: c.title
      })));
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching campaign data:', error);
    throw error;
  }
};

const fetchGoogleAdsDetailed = async (intent) => {
  try {
    // Brand ID'yi al
    const brandId = localStorage.getItem('selectedBrandId') || 
                   localStorage.getItem('currentBrandId') ||
                   sessionStorage.getItem('selectedBrandId') ||
                   'f7e99143-5e9c-4bc8-9052-632613745bb2';
    
    console.log('🔍 Fetching detailed Google Ads data with intent:', intent);
    console.log('🏢 Using brand ID:', brandId);
    
    const params = new URLSearchParams({
      groupBy: intent.groupBy || 'date',
      orderBy: 'google_ads_cost',
      orderDirection: 'DESC'
    });
    
    // Handle custom date range or standard timeframe
    if (intent.timeframe === 'custom' && intent.customDateRange) {
      params.append('startDate', intent.customDateRange.start);
      params.append('endDate', intent.customDateRange.end);
      console.log(`📅 Using custom date range: ${intent.customDateRange.start} to ${intent.customDateRange.end}`);
    } else {
      params.append('dateRange', intent.timeframe || '30');
      console.log(`📅 Using standard date range: ${intent.timeframe || '30'} days`);
    }
    
    // Add filters if specified
    if (intent.campaignName) {
      params.append('campaignName', intent.campaignName);
      console.log(`🎯 Filtering by campaign name: ${intent.campaignName}`);
    }
    
    if (intent.campaignType) {
      params.append('campaignType', intent.campaignType);
      console.log(`🎯 Filtering by campaign type: ${intent.campaignType}`);
    }
    
    const response = await axios.get(`https://api.glowytics.com/api/ai/google-ads-detailed/${brandId}?${params}`, {
      headers: {
        'X-Brand-ID': brandId
      },
      withCredentials: true
    });
    
    console.log('✅ Detailed Google Ads data fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching detailed Google Ads data:', error);
    throw error;
  }
};

const fetchChannelMonthlyRevenue = async (intent) => {
  try {
    const brandId = localStorage.getItem('currentBrandId') || 'f7e99143-5e9c-4bc8-9052-632613745bb2';
    console.log('🔍 Fetching Channel Monthly Revenue with intent:', intent);
    const params = new URLSearchParams({
      dateRange: intent.timeframe || '90'
    });

    if (intent.channel) {
      params.append('channel', intent.channel);
    }
    
    // GroupBy parametresi ekle - aylık kırılım için
    if (intent.groupBy) {
      params.append('groupBy', intent.groupBy);
      console.log(`🗓️ Setting API groupBy parameter to: ${intent.groupBy}`);
    }
    
    const response = await axios.get(`https://api.glowytics.com/api/ai/channel-monthly-revenue/${brandId}?${params}`, {
      headers: { 'X-Brand-ID': brandId },
      withCredentials: true
    });
    
    console.log('✅ Channel Monthly Revenue fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching Channel Monthly Revenue:', error);
    throw error;
  }
};

const fetchProductPerformanceAnalysis = async (intent) => {
  try {
    const brandId = localStorage.getItem('currentBrandId') || 'f7e99143-5e9c-4bc8-9052-632613745bb2';
    console.log('🔍 Fetching Product Performance Analysis with intent:', intent);
    const params = new URLSearchParams({
      analysisType: intent.analysisType || 'products',
    });

    if (intent.brandName) {
      params.append('brandName', intent.brandName);
    }

    if (intent.timeframe === 'custom' && intent.customDateRange) {
      params.append('startDate', intent.customDateRange.start);
      params.append('endDate', intent.customDateRange.end);
    } else {
      params.append('dateRange', intent.timeframe || '30');
    }
    
    if (intent.channel) {
      params.append('channel', intent.channel);
    }
    
    const response = await axios.get(`https://api.glowytics.com/api/ai/product-performance-analysis/${brandId}?${params}`, {
      headers: { 'X-Brand-ID': brandId },
      withCredentials: true
    });
    
    console.log('✅ Product Performance Analysis fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching Product Performance Analysis:', error);
    throw error;
  }
};

const fetchChannelMetricData = async (intent) => {
  try {
    const brandId = localStorage.getItem('currentBrandId') || 'f7e99143-5e9c-4bc8-9052-632613745bb2';
    console.log('🔍 Fetching Channel Metric Data with intent:', intent);
    const params = new URLSearchParams();

    if (intent.channel) {
      params.append('channel', intent.channel);
    }
    if (intent.year) {
      params.append('startDate', `${intent.year}-01-01`);
      params.append('endDate', `${intent.year}-12-31`);
    } else {
      params.append('dateRange', intent.timeframe || '30');
    }
    
    const response = await axios.get(`https://api.glowytics.com/api/ai/channel-monthly-revenue/${brandId}?${params}`, {
      headers: { 'X-Brand-ID': brandId },
      withCredentials: true
    });
    
    console.log('✅ Channel Metric Data fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching Channel Metric Data:', error);
    throw error;
  }
};

const analyzeCampaignData = (data, intent) => {
  if (!data.success || !data.data) {
    return "Üzgünüm, campaign verileri alınamadı. Lütfen brand bağlantılarınızı kontrol edin.";
  }
  
  const { summary, data: campaignData } = data;
  let analysis = [];

  // Handle campaign listing intent for all platforms (multi-platform view)
  if (intent.groupBy === 'campaign_name' && intent.platform === 'all') {
    const googleCampaigns = campaignData.google_ads || [];
    const metaCampaigns = campaignData.meta_ads || [];

    if (googleCampaigns.length === 0 && metaCampaigns.length === 0) {
      return "İstediğiniz tarih aralığında kampanya bulunamadı.";
    }

    let headerDescription = `**${summary.date_range.days} Günlük Tüm Platform Kampanya Listesi** (${summary.date_range.start} - ${summary.date_range.end})`;
    if (intent.customDateRange && intent.customDateRange.type === 'month') {
        const monthNames = { 1: 'Ocak', 2: 'Şubat', 3: 'Mart', 4: 'Nisan', 5: 'Mayıs', 6: 'Haziran', 7: 'Temmuz', 8: 'Ağustos', 9: 'Eylül', 10: 'Ekim', 11: 'Kasım', 12: 'Aralık' };
        headerDescription = `**${monthNames[intent.customDateRange.monthNumber]} Ayı Tüm Platform Kampanya Listesi** (${summary.date_range.start} - ${summary.date_range.end})`;
    }

    analysis.push(headerDescription);

    // Combine and sort all campaigns by ROAS (for optimization view)
    const allCampaigns = [];
    
    // Add Google Ads campaigns with platform identifier
    googleCampaigns.forEach(row => {
      const cost = Number(row.total_cost || 0);
      const revenue = Number(row.total_conversion_value || 0);
      
      // Only calculate ROAS if there's actual spend, otherwise set to 0
      const roas = cost > 0 ? revenue / cost : 0;
      
      allCampaigns.push({
        ...row,
        platform: 'Google Ads',
        platform_icon: '[GOOGLE_ADS_LOGO]',
        roas: roas,
        total_cost: cost,
        total_conversion_value: revenue,
        campaign_type_display: row.campaign_type || 'Unknown'
      });
    });
    
    // Add Meta Ads campaigns with platform identifier  
    metaCampaigns.forEach(row => {
      const cost = Number(row.total_cost || 0);
      const revenue = Number(row.total_conversion_value || 0);
      
      // Only calculate ROAS if there's actual spend, otherwise set to 0
      const roas = cost > 0 ? revenue / cost : 0;
      
      // Try multiple possible campaign name fields for Meta Ads
      const campaignName = row.campaign_name || row.name || row.title || `Campaign_${row.campaign_id}`;
      
      allCampaigns.push({
        ...row,
        campaign_name: campaignName, // Override with best available name
        platform: 'Meta Ads',
        platform_icon: '[META_ADS_LOGO]',
        roas: roas,
        total_cost: cost,
        total_conversion_value: revenue,
        campaign_type_display: row.campaign_objective || 'Unknown'
      });
    });

    // Sort by ROAS descending for optimization insights, but prioritize campaigns with actual spend
    const sortedCampaigns = allCampaigns.sort((a, b) => {
      // First, prioritize campaigns with spend over those without
      if (a.total_cost > 0 && b.total_cost === 0) return -1;
      if (a.total_cost === 0 && b.total_cost > 0) return 1;
      
      // Then sort by ROAS
      return (b.roas || 0) - (a.roas || 0);
    });

    // Calculate meaningful campaign breakdown after allCampaigns is built
    const totalCampaigns = googleCampaigns.length + metaCampaigns.length;
    const activeCampaigns = allCampaigns.filter(c => c.total_cost > 0).length;
    const inactiveCampaigns = totalCampaigns - activeCampaigns;
    
    analysis.push(`🎯 **Kampanya Durumu:**`);
    analysis.push(`   📊 **Toplam:** ${totalCampaigns} kampanya`);
    analysis.push(`   ✅ **Aktif (Harcama Yapan):** ${activeCampaigns} kampanya`);
    if (inactiveCampaigns > 0) {
      analysis.push(`   💤 **İnaktif (Harcama Yapmayan):** ${inactiveCampaigns} kampanya`);
    }
    analysis.push(`   📊 **Google Ads:** ${googleCampaigns.length} | 📱 **Meta Ads:** ${metaCampaigns.length}`);
    analysis.push("");

    // Show top performers (simplified format with filtering)
    analysis.push("🚀 **En İyi Performans Gösteren Kampanyalar (Budget Artırılabilir):**");
    
    // Filter: Min 100₺ spend + ROAS >= 4 for budget scaling recommendations
    const topPerformers = sortedCampaigns
      .filter(c => c.roas >= 4 && (c.total_cost || 0) >= 100)
      .slice(0, 8);
    
    if (topPerformers.length > 0) {
      topPerformers.forEach((row, index) => {
        let performanceIcon = '';
        if (row.roas >= 6) performanceIcon = '🚀';
        else if (row.roas >= 4) performanceIcon = '✅';
        
        const platformDisplay = row.platform === 'Google Ads' ? '📊' : '📱';
        analysis.push(`**${index + 1}. ${row.campaign_name}** ${performanceIcon} ${platformDisplay} ₺${Number(row.total_cost || 0).toLocaleString()} (ROAS: ${row.roas.toFixed(1)})`);
      });
      analysis.push("");
    } else {
      analysis.push("Hiç yüksek performanslı kampanya bulunamadı (4+ ROAS, min 100₺ harcama).");
      analysis.push("");
    }

    analysis.push("⚠️ **Acil Optimizasyon Gereken Kampanyalar (ROAS < 2):**");
    
    // Filter: Min 50₺ spend + ROAS < 2 for optimization focus
    const lowPerformers = sortedCampaigns
      .filter(c => c.roas < 2 && (c.total_cost || 0) >= 50)
      .slice(0, 8);
    
    if (lowPerformers.length > 0) {
      lowPerformers.forEach((row, index) => {
        const platformDisplay = row.platform === 'Google Ads' ? '📊' : '📱';
        analysis.push(`**${index + 1}. ${row.campaign_name}** 🔴 ${platformDisplay} ₺${Number(row.total_cost || 0).toLocaleString()} (ROAS: ${row.roas.toFixed(1)})`);
      });
      analysis.push("");
    } else {
      analysis.push("Tüm kampanyalar kabul edilebilir performans gösteriyor (min 50₺ harcama)!");
      analysis.push("");
    }

    // Add medium performers section (2-4 ROAS range)
    const mediumPerformersFiltered = sortedCampaigns
      .filter(c => c.roas >= 2 && c.roas < 4 && (c.total_cost || 0) >= 100)
      .slice(0, 5);
    
    if (mediumPerformersFiltered.length > 0) {
      analysis.push("⚖️ **Orta Performanslı Kampanyalar (İzleme & Test):**");
      mediumPerformersFiltered.forEach((row, index) => {
        const platformDisplay = row.platform === 'Google Ads' ? '📊' : '📱';
        analysis.push(`**${index + 1}. ${row.campaign_name}** ⚠️ ${platformDisplay} ₺${Number(row.total_cost || 0).toLocaleString()} (ROAS: ${row.roas.toFixed(1)})`);
      });
      analysis.push("");
    }

    // Add optimization insights with filtering applied counts
    const highPerformersFiltered = sortedCampaigns.filter(c => c.roas >= 4 && (c.total_cost || 0) >= 100);
    const lowPerformersFiltered = sortedCampaigns.filter(c => c.roas < 2 && (c.total_cost || 0) >= 50);
    const zeroSpendCampaigns = sortedCampaigns.filter(c => (c.total_cost || 0) === 0);
    const activeSpendingCampaigns = sortedCampaigns.filter(c => (c.total_cost || 0) > 0);

    analysis.push("💡 **Actionable Öneriler:**");
    
    // Only show meaningful recommendations for campaigns with actual spend
    if (highPerformersFiltered.length > 0) {
      analysis.push(`• **✅ Budget Artır:** ${highPerformersFiltered.length} yüksek performanslı kampanyada (ROAS 4+) budget artışı dene`);
    }
    if (lowPerformersFiltered.length > 0) {
      analysis.push(`• **⚠️ Acil Optimize Et:** ${lowPerformersFiltered.length} düşük performanslı kampanyayı (ROAS <2) gözden geçir`);
    }
    if (mediumPerformersFiltered.length > 0) {
      analysis.push(`• **🧪 A/B Test Yap:** ${mediumPerformersFiltered.length} orta performanslı kampanyada (ROAS 2-4) creative/audience testleri başlat`);
    }
    
    // Add zero spend campaign insight if significant
    if (zeroSpendCampaigns.length > 0) {
      analysis.push(`• **📊 İnaktif Kampanyalar:** ${zeroSpendCampaigns.length} kampanya harcama yapmamış - bu kampanyaları gözden geçir veya sil`);
    }
    
    // Add focus strategy based on active spending campaigns
    if (activeSpendingCampaigns.length > 100) {
      analysis.push(`• **🎯 Focus Stratejisi:** ${activeSpendingCampaigns.length} aktif kampanya var - en iyi performans gösteren ${Math.min(30, Math.floor(activeSpendingCampaigns.length * 0.2))}'una odaklan`);
    }

    return analysis.join('\n');
  }

  // Handle campaign listing intent specifically for Meta Ads
  else if (intent.groupBy === 'campaign_name' && intent.platform === 'meta_ads') {
    const metaCampaigns = campaignData.meta_ads || [];

    if (metaCampaigns.length === 0) {
      return "İstediğiniz tarih aralığında Meta Ads kampanyası bulunamadı.";
    }

    let headerDescription = `**${summary.date_range.days} Günlük Meta Ads Kampanya Listesi** (${summary.date_range.start} - ${summary.date_range.end})`;
    if (intent.customDateRange && intent.customDateRange.type === 'month') {
        const monthNames = { 1: 'Ocak', 2: 'Şubat', 3: 'Mart', 4: 'Nisan', 5: 'Mayıs', 6: 'Haziran', 7: 'Temmuz', 8: 'Ağustos', 9: 'Eylül', 10: 'Ekim', 11: 'Kasım', 12: 'Aralık' };
        headerDescription = `**${monthNames[intent.customDateRange.monthNumber]} Ayı Meta Ads Kampanya Listesi** (${summary.date_range.start} - ${summary.date_range.end})`;
    }

    analysis.push(headerDescription);
    analysis.push(`🎯 **Toplam Aktif Kampanya:** ${metaCampaigns.length}`);
    analysis.push("");

    // Sort by cost
    const sortedCampaigns = [...metaCampaigns].sort((a, b) => (b.total_cost || 0) - (a.total_cost || 0));

    sortedCampaigns.slice(0, 20).forEach((row, index) => {
      const roas = Number(row.roas || 0);
      let performanceIcon = '';
      if (roas >= 6) performanceIcon = '🚀';
      else if (roas >= 4) performanceIcon = '✅';
      else if (roas >= 2) performanceIcon = '⚠️';
      else performanceIcon = '🔴';
      
      // Try multiple possible campaign name fields for Meta Ads
      const campaignName = row.campaign_name || row.name || row.title || `Campaign_${row.campaign_id}`;
      
      analysis.push(`**${index + 1}. ${campaignName}** ${performanceIcon}`);
      analysis.push(`   📂 **Amaç:** ${row.campaign_objective}`);
      analysis.push(`   💰 **Harcama:** ₺${Number(row.total_cost || 0).toLocaleString()}`);
      analysis.push(`   💎 **Gelir:** ₺${Number(row.total_conversion_value || 0).toLocaleString()}`);
      analysis.push(`   📈 **ROAS:** ${roas.toFixed(2)}`);
      analysis.push(`   🖱️ **Tıklama:** ${Number(row.total_clicks || 0).toLocaleString()} | 🎯 **Dönüşüm:** ${Number(row.total_conversions || 0)}`);
      analysis.push("");
    });

    if (sortedCampaigns.length > 20) {
      analysis.push(`... ve ${sortedCampaigns.length - 20} kampanya daha.`);
    }

    return analysis.join('\n');
  }

  // Fallback to original summary logic
  let periodDescription;
  if (intent.customDateRange && intent.customDateRange.type === 'month') {
    const monthNames = {
      1: 'Ocak', 2: 'Şubat', 3: 'Mart', 4: 'Nisan', 5: 'Mayıs', 6: 'Haziran',
      7: 'Temmuz', 8: 'Ağustos', 9: 'Eylül', 10: 'Ekim', 11: 'Kasım', 12: 'Aralık'
    };
    const monthName = monthNames[intent.customDateRange.monthNumber];
    periodDescription = `**${monthName} Ayı Campaign Performance Analizi** (${summary.date_range.start} - ${summary.date_range.end})`;
  } else {
    periodDescription = `**${summary.date_range.days} Günlük Campaign Performance Analizi** (${summary.date_range.start} - ${summary.date_range.end})`;
  }
  
  analysis.push(periodDescription);
  analysis.push("");
  
  // Google Ads Analysis
  if (summary.google_ads_summary && (intent.platform === 'all' || intent.platform === 'google_ads')) {
    const gSum = summary.google_ads_summary;
    const avgROAS = gSum.total_cost > 0 ? (gSum.total_conversion_value / gSum.total_cost).toFixed(2) : 0;
    
    analysis.push("[GOOGLE_ADS_LOGO] **Google Ads Performansı:**");
    analysis.push(`• ${gSum.total_campaigns} aktif kampanya`);
    analysis.push(`• ₺${gSum.total_cost.toLocaleString()} toplam harcama`);
    analysis.push(`• ${gSum.total_conversions} conversion`);
    analysis.push(`• ₺${gSum.total_conversion_value.toLocaleString()} toplam gelir`);
    analysis.push(`• **${avgROAS} ROAS**`);
    analysis.push("");
  }
  
  // Meta Ads Analysis
  if (summary.meta_ads_summary && (intent.platform === 'all' || intent.platform === 'meta_ads')) {
    const mSum = summary.meta_ads_summary;
    const avgROAS = mSum.total_cost > 0 ? (mSum.total_conversion_value / mSum.total_cost).toFixed(2) : 0;
    
    analysis.push("[META_ADS_LOGO] **Meta Ads Performansı:**");
    analysis.push(`• ${mSum.total_campaigns} aktif kampanya`);
    analysis.push(`• ₺${mSum.total_cost.toLocaleString()} toplam harcama`);
    analysis.push(`• ${mSum.total_conversions} conversion`);
    analysis.push(`• ₺${mSum.total_conversion_value.toLocaleString()} toplam gelir`);
    analysis.push(`• **${avgROAS} ROAS**`);
    analysis.push("");
  }
  
  // GA4 Analytics Analysis
  if (summary.ga4_summary) {
    const ga4Sum = summary.ga4_summary;
    
    analysis.push("[GA4_LOGO] **Google Analytics Performansı:**");
    analysis.push(`• ${ga4Sum.total_sessions.toLocaleString()} toplam oturum`);
    analysis.push(`• ${ga4Sum.total_users.toLocaleString()} kullanıcı (${ga4Sum.total_new_users.toLocaleString()} yeni)`);
    analysis.push(`• **${ga4Sum.engagement_rate}% engagement rate**`);
    analysis.push(`• ${ga4Sum.total_events.toLocaleString()} toplam event`);
    if (ga4Sum.total_revenue > 0) {
      analysis.push(`• ₺${ga4Sum.total_revenue.toLocaleString()} GA4 tracked revenue`);
    }
    analysis.push("");
  }
  
  // Platform Comparison
  if (intent.platform === 'all' && summary.google_ads_summary && summary.meta_ads_summary) {
    const gROAS = summary.google_ads_summary.total_cost > 0 ? summary.google_ads_summary.total_conversion_value / summary.google_ads_summary.total_cost : 0;
    const mROAS = summary.meta_ads_summary.total_cost > 0 ? summary.meta_ads_summary.total_conversion_value / summary.meta_ads_summary.total_cost : 0;
    
    analysis.push("⚖️ **Platform Karşılaştırması:**");
    analysis.push(`• **En iyi ROAS:** ${gROAS > mROAS ? 'Google Ads' : 'Meta Ads'} (${Math.max(gROAS, mROAS).toFixed(2)})`);
    analysis.push(`• **Toplam gelir:** ₺${(summary.google_ads_summary.total_conversion_value + summary.meta_ads_summary.total_conversion_value).toLocaleString()}`);
    analysis.push("");
  }
  
  // Recommendations based on performance
  analysis.push("💡 **AI Önerileri:**");
  
  if (summary.google_ads_summary) {
    const gROAS = summary.google_ads_summary.total_conversion_value / summary.google_ads_summary.total_cost;
    if (gROAS > 4) {
      analysis.push("• Google Ads ROAS harika! Budget artırma fırsatı olabilir");
    } else if (gROAS < 2) {
      analysis.push("• Google Ads ROAS düşük, kampanya optimizasyonu gerekli");
    }
  }
  
  if (summary.meta_ads_summary) {
    const mROAS = summary.meta_ads_summary.total_conversion_value / summary.meta_ads_summary.total_cost;
    if (mROAS > 4) {
      analysis.push("• Meta Ads ROAS harika! Budget artırma fırsatı olabilir");
    } else if (mROAS < 2) {
      analysis.push("• Meta Ads ROAS düşük, kampanya optimizasyonu gerekli");
    }
  }
  
  // GA4 insights and cross-platform analysis
  if (summary.ga4_summary) {
    const ga4Sum = summary.ga4_summary;
    const totalSpend = (summary.google_ads_summary?.total_cost || 0) + (summary.meta_ads_summary?.total_cost || 0);
    
    if (ga4Sum.engagement_rate < 40) {
      analysis.push("• Engagement rate düşük, landing page optimizasyonu gerekebilir");
    }
    
    if (totalSpend > 0 && ga4Sum.total_sessions > 0) {
      const costPerSession = totalSpend / ga4Sum.total_sessions;
      if (costPerSession > 5) {
        analysis.push("• Cost per session yüksek, hedefleme optimizasyonu düşünün");
      } else if (costPerSession < 1) {
        analysis.push("• Cost per session mükemmel, budget scaling fırsatı!");
      }
    }
    
    // New vs returning user analysis
    const returningUsers = ga4Sum.total_users - ga4Sum.total_new_users;
    const newUserRate = ga4Sum.total_users > 0 ? (ga4Sum.total_new_users / ga4Sum.total_users * 100).toFixed(1) : 0;
    
    if (newUserRate > 80) {
      analysis.push(`• Yüksek yeni kullanıcı oranı (%${newUserRate}) - remarketing kampanyalarını güçlendirin`);
    } else if (newUserRate < 30) {
      analysis.push(`• Düşük yeni kullanıcı oranı (%${newUserRate}) - acquisition kampanyalarına odaklanın`);
    }
  }
  
  // 7+2 day methodology recommendation
  analysis.push("• 📊 7+2 günlük metodolojiye göre son 2 günün performansını karşılaştırarak budget optimizasyonu yapabiliriz");
  
  return analysis.join('\n');
};

// 🎯 ROAS Trend Analysis - 5+4 Day Methodology for Budget Optimization
const analyzeROASTrendFor5Plus4Days = (data, intent) => {
  if (!data.success || !data.data) {
    return "Üzgünüm, ROAS trend analizi için veriler alınamadı. Lütfen brand bağlantılarınızı kontrol edin.";
  }
  
  const { summary, data: campaignData } = data;
  let analysis = [];
  
  analysis.push("🎯 **ROAS Trend Analizi - 5+4 Günlük Budget Optimization**");
  analysis.push("");
  analysis.push(`📅 **Analiz Dönemi:** ${summary.date_range.start} - ${summary.date_range.end} (${summary.date_range.days} gün)`);
  analysis.push("");
  analysis.push("📊 **Metodoloji:** İlk 5 günün ROAS ortalaması vs Son 4 günün ROAS ortalaması");
  analysis.push("");
  
  // Process both Google Ads and Meta Ads
  const allCampaigns = [];
  
  // Google Ads campaigns
  if (campaignData.google_ads && Array.isArray(campaignData.google_ads)) {
    campaignData.google_ads.forEach(row => {
      allCampaigns.push({
        platform: 'Google Ads',
        campaign_id: row.campaign_id,
        campaign_name: row.campaign_name,
        campaign_type: row.campaign_type,
        date: row.date,
        cost: row.total_cost,
        conversion_value: row.total_conversion_value,
        roas: row.roas || (row.total_cost > 0 ? (row.total_conversion_value / row.total_cost).toFixed(2) : 0)
      });
    });
  }
  
  // Meta Ads campaigns
  if (campaignData.meta_ads && Array.isArray(campaignData.meta_ads)) {
    campaignData.meta_ads.forEach(row => {
      allCampaigns.push({
        platform: 'Meta Ads',
        campaign_id: row.campaign_id,
        campaign_name: row.campaign_name,
        campaign_type: row.campaign_objective,
        date: row.date,
        cost: row.total_cost,
        conversion_value: row.total_conversion_value,
        roas: row.roas || (row.total_cost > 0 ? (row.total_conversion_value / row.total_cost).toFixed(2) : 0)
      });
    });
  }
  
  if (allCampaigns.length === 0) {
    return "❌ 9 günlük period'ta kampanya verisi bulunamadı. Lütfen farklı bir tarih aralığı deneyin.";
  }
  
  // Group campaigns by campaign_id and calculate 5+4 day trends
  const campaignGroups = {};
  allCampaigns.forEach(campaign => {
    const key = `${campaign.platform}_${campaign.campaign_id}`;
    if (!campaignGroups[key]) {
      campaignGroups[key] = {
        platform: campaign.platform,
        campaign_name: campaign.campaign_name,
        campaign_type: campaign.campaign_type,
        daily_data: []
      };
    }
    campaignGroups[key].daily_data.push({
      date: campaign.date,
      cost: parseFloat(campaign.cost) || 0,
      conversion_value: parseFloat(campaign.conversion_value) || 0,
      roas: parseFloat(campaign.roas) || 0
    });
  });
  
  // Analyze each campaign with 5+4 methodology
  const campaignAnalyses = [];
  
  Object.keys(campaignGroups).forEach(campaignKey => {
    const campaign = campaignGroups[campaignKey];
    
    // Sort by date to ensure chronological order
    campaign.daily_data.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    if (campaign.daily_data.length < 6) {
      return; // Skip campaigns with insufficient data
    }
    
    // Split into first 5 days and last 4 days
    const first5Days = campaign.daily_data.slice(0, 5);
    const last4Days = campaign.daily_data.slice(-4);
    
    // Calculate averages
    const first5AvgROAS = first5Days.reduce((sum, day) => sum + day.roas, 0) / first5Days.length;
    const last4AvgROAS = last4Days.reduce((sum, day) => sum + day.roas, 0) / last4Days.length;
    
    const first5TotalCost = first5Days.reduce((sum, day) => sum + day.cost, 0);
    const last4TotalCost = last4Days.reduce((sum, day) => sum + day.cost, 0);
    
    // Determine trend and recommendation
    const roasChange = last4AvgROAS - first5AvgROAS;
    const roasChangePercent = first5AvgROAS > 0 ? ((roasChange / first5AvgROAS) * 100).toFixed(1) : 0;
    
    let recommendation = '';
    let recommendationType = '';
    let confidence = '';
    
    if (roasChange > 0.5) {
      recommendation = '🟢 BÜTÇE ARTIR';
      recommendationType = 'INCREASE';
      confidence = roasChange > 1.0 ? 'YÜksek Güven' : 'Orta Güven';
    } else if (roasChange < -0.5) {
      recommendation = '🔴 BÜTÇE AZALT';
      recommendationType = 'DECREASE';
      confidence = roasChange < -1.0 ? 'Yüksek Güven' : 'Orta Güven';
    } else {
      recommendation = '🟡 BÜTÇE KORU';
      recommendationType = 'MAINTAIN';
      confidence = 'Kararlı';
    }
    
    campaignAnalyses.push({
      platform: campaign.platform,
      campaign_name: campaign.campaign_name,
      campaign_type: campaign.campaign_type,
      first5AvgROAS: first5AvgROAS.toFixed(2),
      last4AvgROAS: last4AvgROAS.toFixed(2),
      roasChange: roasChange.toFixed(2),
      roasChangePercent: roasChangePercent,
      first5TotalCost: first5TotalCost.toFixed(0),
      last4TotalCost: last4TotalCost.toFixed(0),
      recommendation: recommendation,
      recommendationType: recommendationType,
      confidence: confidence
    });
  });
  
  if (campaignAnalyses.length === 0) {
    return "❌ Analiz için yeterli günlük veri bulunamadı. En az 6 günlük veri gereklidir.";
  }
  
  // Sort by ROAS change (biggest improvements first)
  campaignAnalyses.sort((a, b) => parseFloat(b.roasChange) - parseFloat(a.roasChange));
  
  // Add campaign-by-campaign analysis
  analysis.push("📋 **Kampanya Bazında Budget Önerileri:**");
  analysis.push("");
  
  campaignAnalyses.forEach((camp, index) => {
    const platformIcon = camp.platform === 'Google Ads' ? '[GOOGLE_ADS_LOGO]' : '[META_ADS_LOGO]';
    
    analysis.push(`**${index + 1}. ${camp.campaign_name}** ${platformIcon}`);
    analysis.push(`   • Tip: ${camp.campaign_type}`);
    analysis.push(`   • İlk 5 Gün ROAS: **${camp.first5AvgROAS}** (₺${Number(camp.first5TotalCost).toLocaleString()} harcama)`);
    analysis.push(`   • Son 4 Gün ROAS: **${camp.last4AvgROAS}** (₺${Number(camp.last4TotalCost).toLocaleString()} harcama)`);
    analysis.push(`   • ROAS Değişimi: **${camp.roasChange}** (${camp.roasChangePercent}%)`);
    analysis.push(`   • **${camp.recommendation}** - ${camp.confidence}`);
    analysis.push("");
  });
  
  // Summary insights
  const increaseCount = campaignAnalyses.filter(c => c.recommendationType === 'INCREASE').length;
  const decreaseCount = campaignAnalyses.filter(c => c.recommendationType === 'DECREASE').length;
  const maintainCount = campaignAnalyses.filter(c => c.recommendationType === 'MAINTAIN').length;
  
  analysis.push("📊 **Özet Öneriler:**");
  analysis.push(`• ${increaseCount} kampanya için bütçe artırımı öneriliyor 🟢`);
  analysis.push(`• ${decreaseCount} kampanya için bütçe azaltımı öneriliyor 🔴`);
  analysis.push(`• ${maintainCount} kampanya için mevcut bütçe korunmalı 🟡`);
  analysis.push("");
  
  // Best and worst performers
  if (campaignAnalyses.length > 0) {
    const bestPerformer = campaignAnalyses[0];
    const worstPerformer = campaignAnalyses[campaignAnalyses.length - 1];
    
    analysis.push("🏆 **En İyi Performer:**");
    analysis.push(`• ${bestPerformer.campaign_name} (+${bestPerformer.roasChangePercent}% ROAS artışı)`);
    analysis.push("");
    
    if (parseFloat(worstPerformer.roasChange) < 0) {
      analysis.push("⚠️ **Dikkat Gereken Kampanya:**");
      analysis.push(`• ${worstPerformer.campaign_name} (${worstPerformer.roasChangePercent}% ROAS düşüşü)`);
      analysis.push("");
    }
  }
  
  analysis.push("💡 **Aksiyon Planı:**");
  analysis.push("1. 🟢 Yüksek trend gösteren kampanyalara %10-20 budget artışı yapın");
  analysis.push("2. 🔴 Düşüş gösteren kampanyaların hedeflemelerini gözden geçirin");
  analysis.push("3. 🟡 Stabil kampanyaları mevcut haliyle sürdürün");
  analysis.push("4. 📈 1 hafta sonra tekrar analiz yapın ve sonuçları değerlendirin");
  
  return analysis.join('\n');
};

const analyzeGoogleAdsDetailed = (data, intent) => {
  if (!data.success || !data.data || data.data.length === 0) {
    return "Üzgünüm, detaylı Google Ads verileri alınamadı. Lütfen tarih aralığını kontrol edin.";
  }
  
  const { summary, data: detailedData, query_info } = data;
  let analysis = [];
  
  // Header based on grouping
  let headerDescription;
  if (intent.customDateRange && intent.customDateRange.type === 'month') {
    const monthNames = {
      1: 'Ocak', 2: 'Şubat', 3: 'Mart', 4: 'Nisan', 5: 'Mayıs', 6: 'Haziran',
      7: 'Temmuz', 8: 'Ağustos', 9: 'Eylül', 10: 'Ekim', 11: 'Kasım', 12: 'Aralık'
    };
    const monthName = monthNames[intent.customDateRange.monthNumber];
    
    if (query_info.group_by === 'campaign_name') {
      headerDescription = `📊 **${monthName} Ayı Google Ads Kampanya Listesi**`;
    } else {
      headerDescription = `📊 **${monthName} Ayı Detaylı Google Ads Analizi**`;
    }
  } else {
    if (query_info.group_by === 'campaign_name') {
      headerDescription = `📊 **${summary.date_range.days} Günlük Google Ads Kampanya Listesi**`;
    } else {
      headerDescription = `📊 **${summary.date_range.days} Günlük Detaylı Google Ads Analizi**`;
    }
  }
  
  analysis.push(headerDescription);
  analysis.push(`🗓️ **Tarih Aralığı:** ${summary.date_range.start} - ${summary.date_range.end}`);
  
  if (query_info.group_by === 'campaign_name') {
    analysis.push(`🎯 **Toplam Aktif Kampanya:** ${summary.total_records}`);
  } else {
    analysis.push(`🔍 **Gruplama:** ${query_info.group_by === 'date' ? 'Tarih Bazında' : query_info.group_by === 'campaign_name' ? 'Kampanya Bazında' : query_info.group_by === 'campaign_type' ? 'Kampanya Türü Bazında' : 'Detaylı Kırılım'}`);
    analysis.push(`📈 **Toplam Kayıt:** ${summary.total_records}`);
  }
  analysis.push("");
  
  // Summary totals
  if (summary.totals) {
    analysis.push("💰 **Genel Özet:**");
    analysis.push(`• **Toplam Harcama:** ₺${Number(summary.totals.total_cost).toLocaleString()}`);
    analysis.push(`• **Toplam Gelir:** ₺${Number(summary.totals.total_revenue).toLocaleString()}`);
    analysis.push(`• **Toplam Oturum:** ${Number(summary.totals.total_sessions).toLocaleString()}`);
    analysis.push(`• **Genel ROAS:** ${summary.totals.overall_roas}`);
    analysis.push(`• **Oturum Başına Maliyet:** ₺${summary.totals.avg_cost_per_session}`);
    analysis.push("");
  }
  
  // Detailed breakdown based on groupBy
  switch (query_info.group_by) {
    case 'date':
      analysis.push("📅 **Günlük Performans:**");
      detailedData.slice(0, 10).forEach(row => {
        const date = new Date(row.date).toLocaleDateString('tr-TR');
        const cost = Number(row.total_cost || 0).toLocaleString();
        const revenue = Number(row.total_revenue || 0).toLocaleString();
        const roas = Number(row.roas || 0);
        const campaignCount = Number(row.campaign_count || 0);
        
        analysis.push(`• **${date}:** ₺${cost} harcama, ₺${revenue} gelir, ${roas} ROAS, ${campaignCount} kampanya`);
      });
      break;
      
    case 'campaign_name':
      analysis.push("🎯 **Kampanya Listesi ve Performansları:**");
      analysis.push("");
      
      // Sort campaigns by cost (highest first) for better display
      const sortedCampaigns = [...detailedData].sort((a, b) => Number(b.total_cost || 0) - Number(a.total_cost || 0));
      
      sortedCampaigns.slice(0, 20).forEach((row, index) => {
        const campaignName = row.campaign_name || 'Bilinmeyen Kampanya';
        const campaignType = (row.campaign_type || '').replace('AdvertisingChannelType.', '');
        const cost = Number(row.total_cost || 0);
        const revenue = Number(row.total_revenue || 0);
        const ga4Revenue = Number(row.total_ga4_revenue || 0);
        const roas = Number(row.roas || 0);
        const clicks = Number(row.total_clicks || 0);
        const sessions = Number(row.total_sessions || 0);
        const conversions = Number(row.total_conversions || 0);
        
        // Performance indicators
        let performanceIcon = '';
        if (roas >= 6) performanceIcon = '🚀';
        else if (roas >= 4) performanceIcon = '✅';
        else if (roas >= 2) performanceIcon = '⚠️';
        else performanceIcon = '🔴';
        
        analysis.push(`**${index + 1}. ${campaignName}** ${performanceIcon}`);
        if (campaignType) {
          analysis.push(`   📂 **Tür:** ${campaignType}`);
        }
        analysis.push(`   💰 **Harcama:** ₺${cost.toLocaleString()}`);
        analysis.push(`   💎 **Google Ads Gelir:** ₺${revenue.toLocaleString()}`);
        if (ga4Revenue > 0) {
          analysis.push(`   📊 **GA4 Gelir:** ₺${ga4Revenue.toLocaleString()}`);
        }
        analysis.push(`   📈 **ROAS:** ${roas} ${performanceIcon}`);
        analysis.push(`   🖱️ **Tıklama:** ${clicks.toLocaleString()} | 👥 **Oturum:** ${sessions.toLocaleString()}`);
        if (conversions > 0) {
          analysis.push(`   🎯 **Dönüşüm:** ${conversions}`);
        }
        analysis.push("");
      });
      
      if (detailedData.length > 20) {
        analysis.push(`... ve ${detailedData.length - 20} kampanya daha. Daha fazla detay için tarih aralığını daraltabilirsiniz.`);
        analysis.push("");
      }
      break;
      
    case 'campaign_type':
      analysis.push("📋 **Kampanya Türü Performansı:**");
      detailedData.forEach(row => {
        const campaignType = (row.campaign_type || '').replace('AdvertisingChannelType.', '');
        const cost = Number(row.total_cost || 0).toLocaleString();
        const revenue = Number(row.total_revenue || 0).toLocaleString();
        const roas = Number(row.roas || 0);
        const campaignCount = Number(row.campaign_count || 0);
        
        analysis.push(`• **${campaignType}:**`);
        analysis.push(`  - ₺${cost} harcama, ₺${revenue} gelir, ${roas} ROAS`);
        analysis.push(`  - ${campaignCount} kampanya`);
      });
      break;
      
    case 'date_campaign':
      analysis.push("🔍 **Detaylı Günlük x Kampanya Kırılımı:**");
      const groupedByDate = {};
      detailedData.slice(0, 20).forEach(row => {
        const date = new Date(row.date).toLocaleDateString('tr-TR');
        if (!groupedByDate[date]) {
          groupedByDate[date] = [];
        }
        groupedByDate[date].push(row);
      });
      
      Object.entries(groupedByDate).slice(0, 5).forEach(([date, campaigns]) => {
        analysis.push(`• **${date}:**`);
        campaigns.slice(0, 3).forEach(campaign => {
          const campaignName = campaign.campaign_name || 'Bilinmeyen';
          const cost = Number(campaign.cost || 0).toLocaleString();
          const revenue = Number(campaign.revenue || 0).toLocaleString();
          const roas = Number(campaign.roas || 0);
          
          analysis.push(`  - ${campaignName}: ₺${cost} → ₺${revenue} (${roas} ROAS)`);
        });
        
        if (campaigns.length > 3) {
          analysis.push(`  - ... ve ${campaigns.length - 3} kampanya daha`);
        }
      });
      break;
  }
  
  analysis.push("");
  
  // Performance insights
  if (query_info.group_by === 'campaign_name') {
    analysis.push("🎯 **Kampanya Performans İçgörüleri:**");
    
    const highPerformers = detailedData.filter(row => Number(row.roas || 0) >= 6);
    const goodPerformers = detailedData.filter(row => Number(row.roas || 0) >= 4 && Number(row.roas || 0) < 6);
    const lowPerformers = detailedData.filter(row => Number(row.roas || 0) < 2);
    
    if (highPerformers.length > 0) {
      analysis.push(`• **🚀 Mükemmel Performans:** ${highPerformers.length} kampanya (6+ ROAS)`);
      const topCampaign = highPerformers.reduce((best, current) => 
        Number(current.roas || 0) > Number(best.roas || 0) ? current : best
      );
      analysis.push(`  - En iyi: ${topCampaign.campaign_name} (${Number(topCampaign.roas || 0)} ROAS)`);
    }
    
    if (goodPerformers.length > 0) {
      analysis.push(`• **✅ İyi Performans:** ${goodPerformers.length} kampanya (4-6 ROAS)`);
    }
    
    if (lowPerformers.length > 0) {
      analysis.push(`• **🔴 Düşük Performans:** ${lowPerformers.length} kampanya (2 ROAS altı)`);
      analysis.push(`  - Bu kampanyalar optimizasyon veya duraklatma gerektirebilir`);
    }
    
    // Budget recommendations
    const totalSpend = detailedData.reduce((sum, row) => sum + Number(row.total_cost || 0), 0);
    const highPerformerSpend = highPerformers.reduce((sum, row) => sum + Number(row.total_cost || 0), 0);
    const lowPerformerSpend = lowPerformers.reduce((sum, row) => sum + Number(row.total_cost || 0), 0);
    
    if (totalSpend > 0) {
      const highPerformerPercent = ((highPerformerSpend / totalSpend) * 100).toFixed(1);
      const lowPerformerPercent = ((lowPerformerSpend / totalSpend) * 100).toFixed(1);
      
      analysis.push("");
      analysis.push("💰 **Budget Dağılımı:**");
      analysis.push(`• Yüksek performanslı kampanyalar: %${highPerformerPercent} (₺${highPerformerSpend.toLocaleString()})`);
      analysis.push(`• Düşük performanslı kampanyalar: %${lowPerformerPercent} (₺${lowPerformerSpend.toLocaleString()})`);
    }
  } else {
    analysis.push("🎯 **Performans İçgörüleri:**");
    
    if (query_info.group_by === 'campaign_type') {
      const bestType = detailedData.reduce((best, current) => 
        Number(current.roas || 0) > Number(best.roas || 0) ? current : best
      );
      if (bestType) {
        const type = (bestType.campaign_type || '').replace('AdvertisingChannelType.', '');
        analysis.push(`• **En iyi kampanya türü:** ${type} (${Number(bestType.roas || 0)} ROAS)`);
      }
    }
  }
  
  // Recommendations
  analysis.push("");
  analysis.push("💡 **Öneriler:**");
  
  if (query_info.group_by === 'campaign_name') {
    const highPerformers = detailedData.filter(row => Number(row.roas || 0) >= 6);
    const lowPerformers = detailedData.filter(row => Number(row.roas || 0) < 2);
    
    if (highPerformers.length > 0) {
      analysis.push(`• **Budget artırımı:** ${highPerformers.length} yüksek performanslı kampanyaya budget artışı öneririm`);
    }
    
    if (lowPerformers.length > 0) {
      analysis.push(`• **Optimizasyon gerekli:** ${lowPerformers.length} kampanya için hedefleme, reklam metni veya açılış sayfası optimizasyonu yapın`);
    }
    
    analysis.push("• **7+2 gün metodolojisi:** Son 2 günün performansını 7 günlük ortalama ile karşılaştırarak günlük budget ayarlamaları yapın");
  } else {
    analysis.push("• Yüksek ROAS'lı kampanyalara budget artırımı düşünün");
    analysis.push("• Düşük performanslı kampanyaları duraklatın veya optimize edin");
    analysis.push("• En iyi performans gösteren kampanya türlerini genişletin");
  }
  
  return analysis.join('\n');
};

const analyzeProductPerformance = (data, intent) => {
  if (!data.success || !data.data || data.data.length === 0) {
    return `Üzgünüm, istenen kriterlere uygun ürün performansı verisi bulunamadı.`;
  }

  const { data: performanceData, query_info } = data;
  let analysis = [];
  
  // Handle specific metric requests first
  const brandName = intent.brandName;
  if (intent.metric === 'conversion_rate' && brandName) {
    const brandData = performanceData.find(item => item.item_brand && item.item_brand.toLowerCase().includes(brandName.toLowerCase()));
    
    if (brandData) {
      const purchases = Number(brandData.total_purchases || 0);
      const views = Number(brandData.total_views || 0);
      const conversionRate = views > 0 ? ((purchases / views) * 100).toFixed(3) : 0;
      
      const monthName = intent.customDateRange ? ` ${new Date(intent.customDateRange.start).toLocaleString('tr-TR', { month: 'long' })} ayı` : '';

      return `${brandData.item_brand} markasının${monthName} conversion rate'i: %${conversionRate}.`;
    } else {
       return `${brandName} markası için belirtilen tarih aralığında veri bulunamadı.`;
    }
  }

  // Handle specific brand performance request
  if (brandName) {
    const brandData = performanceData.find(item => item.item_brand && item.item_brand.toLowerCase().includes(brandName.toLowerCase()));
    if (brandData) {
      const revenue = `₺${Number(brandData.total_revenue || 0).toLocaleString('tr-TR', {maximumFractionDigits: 0})}`;
      const purchases = `${Number(brandData.total_purchases || 0).toLocaleString()} satış`;
      const views = `${Number(brandData.total_views || 0).toLocaleString()} görüntülenme`;

      analysis.push(`**${brandData.item_brand} Markası Performansı:**`);
      analysis.push(`  • **Gelir:** ${revenue}`);
      analysis.push(`  • **Satış Adedi:** ${purchases}`);
      analysis.push(`  • **Görüntülenme:** ${views}`);
      return analysis.join('\n');
    }
  }

  // Fallback to list if no specific metric or if brand not found
  const analysisTypeName = 
    query_info.analysisType === 'brands' ? 'Marka' :
    query_info.analysisType === 'categories' ? 'Kategori' : 'Ürün';

  let dateDescription = `Son ${query_info.dateRange} Günlük`;
  if (intent.timeframe === 'custom' && intent.customDateRange) {
    const monthNames = { 1: 'Ocak', 2: 'Şubat', 3: 'Mart', 4: 'Nisan', 5: 'Mayıs', 6: 'Haziran', 7: 'Temmuz', 8: 'Ağustos', 9: 'Eylül', 10: 'Ekim', 11: 'Kasım', 12: 'Aralık' };
    dateDescription = `${monthNames[intent.customDateRange.monthNumber]} Ayı`;
  }

  analysis.push(`**${dateDescription} En İyi ${analysisTypeName} Performansı**`);
  analysis.push("");

  performanceData.forEach((item, index) => {
    let itemName = '';
    if (query_info.analysisType === 'brands') itemName = item.item_brand;
    else if (query_info.analysisType === 'categories') itemName = item.item_category;
    else itemName = item.item_name;

    const revenue = `₺${Number(item.total_revenue || 0).toLocaleString('tr-TR', {maximumFractionDigits: 0})}`;
    const purchases = `${Number(item.total_purchases || 0).toLocaleString()} satış`;
    const views = `${Number(item.total_views || 0).toLocaleString()} görüntülenme`;

    analysis.push(`**${index + 1}. ${itemName}**`);
    analysis.push(`  • **Gelir:** ${revenue}`);
    analysis.push(`  • **Satış Adedi:** ${purchases}`);
    analysis.push(`  • **Görüntülenme:** ${views}`);
    analysis.push("");
  });

  return analysis.join('\n');
};

const analyzeSingleChannelPerformance = (data, intent) => {
  if (!data.success || !data.data || data.data.length === 0) {
    return null; // Return null instead of a string
  }

  const { data: channelData, query_info } = data;

  const channelName = intent.channel.charAt(0).toUpperCase() + intent.channel.slice(1);
  const dateRangeInDays = parseInt(query_info.dateRange || '120', 10);
  const dateRangeInMonths = Math.round(dateRangeInDays / 30);
  
  const monthlyBreakdown = [];
  const overallSummary = { revenue: 0, cost: 0, sessions: 0, purchases: 0 };

  channelData.forEach(row => {
    const revenue = Number(row.total_revenue || 0);
    const cost = Number(row.total_cost || 0);
    const sessions = Number(row.total_sessions || 0);
    const purchases = Number(row.total_purchases || 0);
    const roas = Number(row.roas || 0);

    monthlyBreakdown.push({
      month: new Date(row.month + '-02').toLocaleString('tr-TR', { month: 'long', year: 'numeric' }),
      revenue,
      sessions,
      purchases,
      cost: cost > 0 ? cost : undefined, // Only include cost if it's greater than 0
      roas: cost > 0 ? roas : undefined,  // Only include ROAS if cost is greater than 0
    });

    overallSummary.revenue += revenue;
    overallSummary.cost += cost;
    overallSummary.sessions += sessions;
    overallSummary.purchases += purchases;
  });
  
  const averageRoas = overallSummary.cost > 0 ? (overallSummary.revenue / overallSummary.cost) : 0;
  overallSummary.roas = averageRoas;
  
  // Return a structured JSON object for the AI
  return {
    channelName,
    timeframeInMonths: dateRangeInMonths,
    monthlyBreakdown,
    overallSummary,
  };
};

const analyzeChannelMonthlyRevenue = (data, intent) => {
  if (!data.success || !data.data || data.data.length === 0) {
    return null; // Return null if no data
  }

  const { data: revenueData, query_info } = data;
  
  const timeframeInDays = parseInt(query_info.dateRange || '120', 10);
  const timeframeInMonths = Math.round(timeframeInDays / 30);
  
  const monthlyBreakdown = {};
  
  // Get current date details for projection
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-indexed
  const currentDayOfMonth = today.getDate();

  revenueData.forEach(row => {
    const [year, month] = row.month.split('-').map(Number);
    const monthIndex = month - 1; // 0-indexed
    const monthName = new Date(year, monthIndex).toLocaleString('tr-TR', { month: 'long', year: 'numeric' });

    if (!monthlyBreakdown[monthName]) {
      monthlyBreakdown[monthName] = [];
    }
    
    const cost = Number(row.total_cost || 0);
    const revenue = Number(row.total_revenue || 0);
    const sessions = Number(row.total_sessions || 0);
    const purchases = Number(row.total_purchases || 0);

    const isCurrentMonthFlag = year === currentYear && monthIndex === currentMonth;
    let projection = {};

    if (isCurrentMonthFlag && currentDayOfMonth > 0) {
      const daysInMonth = new Date(year, month, 0).getDate();
      if (currentDayOfMonth < daysInMonth) {
        const dailyAvgRevenue = revenue / currentDayOfMonth;
        const dailyAvgSessions = sessions / currentDayOfMonth;
        projection = {
          projectedRevenue: dailyAvgRevenue * daysInMonth,
          projectedSessions: dailyAvgSessions * daysInMonth,
        };
      }
    }

    monthlyBreakdown[monthName].push({
      channel: row.channel,
      revenue,
      sessions,
      purchases,
      cost: cost > 0 ? cost : undefined,
      roas: cost > 0 ? Number(row.roas || 0) : undefined,
      isCurrentMonth: isCurrentMonthFlag,
      ...projection
    });
  });
  
  // Return a structured JSON object
  return {
    query: {
      channel: query_info.channel || 'All Channels',
      timeframeInMonths,
    },
    monthlyBreakdown,
  };
};

const analyzeChannelMetric = (data, intent) => {
  if (!data.success || !data.data || data.data.length === 0) {
    return `Üzgünüm, ${intent.year || ''} yılı için ${intent.channel} kanalından gelen veri bulunamadı.`;
  }

  const { data: metricData } = data;
  let analysis = [];
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // JS months are 0-indexed
  const currentDayOfMonth = today.getDate();

  const monthlyPerformance = {};

  metricData.forEach(row => {
    const [year, month] = row.month.split('-').map(Number);
    const monthName = new Date(row.month + '-02').toLocaleString('tr-TR', { month: 'long' });
    
    if (!monthlyPerformance[monthName]) {
      monthlyPerformance[monthName] = { sessions: 0, revenue: 0, purchases: 0, year, month };
    }
    monthlyPerformance[monthName].sessions += Number(row.total_sessions || 0);
    monthlyPerformance[monthName].revenue += Number(row.total_revenue || 0);
    monthlyPerformance[monthName].purchases += Number(row.total_purchases || 0);
  });
  
  // Projection logic
  let projectionInsight = '';
  const currentMonthName = today.toLocaleString('tr-TR', { month: 'long' });
  
  if (monthlyPerformance[currentMonthName] && intent.year === currentYear) {
    const currentMonthData = monthlyPerformance[currentMonthName];
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
    
    if (currentDayOfMonth < daysInMonth) {
      const dailyAverageRevenue = currentMonthData.revenue / currentDayOfMonth;
      const projectedRevenue = dailyAverageRevenue * daysInMonth;

      // Find the best historical month (excluding current)
      const historicalMonths = Object.entries(monthlyPerformance).filter(([name]) => name !== currentMonthName);
      if (historicalMonths.length > 0) {
        const bestHistoricalMonth = historicalMonths.reduce((best, [, data]) => data.revenue > best.revenue ? data : best, {revenue: 0});
        const bestHistoricalMonthName = Object.entries(monthlyPerformance).find(([,data]) => data.revenue === bestHistoricalMonth.revenue)?.[0];
        
        if (projectedRevenue > bestHistoricalMonth.revenue) {
          projectionInsight = `**Öngörü:** Mevcut gidişatla, **${currentMonthName} ayı (tahmini ₺${projectedRevenue.toLocaleString('tr-TR', {maximumFractionDigits: 0})})**, şu ana kadarki en iyi ay olan **${bestHistoricalMonthName}'ı (₺${bestHistoricalMonth.revenue.toLocaleString('tr-TR', {maximumFractionDigits: 0})}) geçerek rekor kırabilir!**`;
        }
      }
    }
  }

  if (projectionInsight) {
    analysis.push(projectionInsight);
    analysis.push("");
  }
  
  const year = intent.year;
  const channel = intent.channel.charAt(0).toUpperCase() + intent.channel.slice(1);
  const totalSessions = Object.values(monthlyPerformance).reduce((sum, item) => sum + item.sessions, 0);
  const dateRangeText = year ? `${year} Yılı` : 'Son 30 Gün';

  analysis.push(`**${dateRangeText} - ${channel} Kanalı Performans Özeti**`);
  analysis.push("");

  if (intent.metric === 'summary') {
    const totalRevenue = Object.values(monthlyPerformance).reduce((sum, item) => sum + item.revenue, 0);
    const totalPurchases = Object.values(monthlyPerformance).reduce((sum, item) => sum + item.purchases, 0);
    analysis.push(`• **Toplam Oturum:** ${totalSessions.toLocaleString()}`);
    analysis.push(`• **Toplam Gelir:** ₺${totalRevenue.toLocaleString('tr-TR', {maximumFractionDigits: 0})}`);
    analysis.push(`• **Toplam Satış:** ${totalPurchases.toLocaleString()} adet`);
    analysis.push("");
  }
  
  analysis.push("**Aylık Oturum Dağılımı:**");

  // Sort months chronologically for display
  const sortedMonths = Object.keys(monthlyPerformance).sort((a, b) => {
      const dateA = new Date(monthlyPerformance[a].year, monthlyPerformance[a].month - 1);
      const dateB = new Date(monthlyPerformance[b].year, monthlyPerformance[b].month - 1);
      return dateB - dateA; // Descending
  });

  for (const monthName of sortedMonths) {
    const isCurrentMonth = monthName === currentMonthName && monthlyPerformance[monthName].year === currentYear;
    analysis.push(`• **${monthName}:** ${monthlyPerformance[monthName].sessions.toLocaleString()} oturum ${isCurrentMonth ? '(şu ana kadar)' : ''}`);
  }
  
  analysis.push("");
  analysis.push(`**Genel Toplam Oturum:** ${totalSessions.toLocaleString()}`);
  
  return analysis.join('\n');
};

// OpenAI API isteklerini yönetecek fonksiyon
const sendMessageToOpenAI = async (messages, userId) => {
  try {
    // Önce kullanıcının sorusunu analiz et
    const lastUserMessage = messages[messages.length - 1];
    let userQueryContent = lastUserMessage.content;

    // IMPROVED CONTEXT HANDLING for follow-up questions
    // "bunu da ekle", "session ile göster" gibi takip soruları için bir önceki sorguyla birleştir.
    if (messages.length > 2) {
      const prevUserMessage = messages.filter(m => m.sender === 'user').slice(-2)[0];
      const followUpKeywords = ['dahil et', 'ekle', 'ile birlikte', 'göster', 'peki ya', 'what about', 'include', 'with'];
      
      if (prevUserMessage && followUpKeywords.some(kw => userQueryContent.toLowerCase().includes(kw))) {
         userQueryContent = prevUserMessage.content + ' ' + userQueryContent;
         console.log('🧠 Context merged for follow-up query:', userQueryContent);
      }
    }
    
    if (lastUserMessage && lastUserMessage.sender === 'user') {
      // 1) KULLANICI HAFIZASINI ÖNCE YÜKLE ve kişiselleştirme hazırla
      const userMemory = await getUserMemory(userId);
      const rememberedName = extractUserName(userMemory);
      const personalizedPrefix = buildPersonalizedPrefix(rememberedName, 'tr');
      
      const intent = analyzeUserQuery(userQueryContent);
      
      console.log('🤔 Detected intent:', intent);
      
      // TOTAL REVENUE HANDLER - fetch all channels data for AI analysis
      if (intent.type === 'total_revenue' && intent.confidence > 0.8) {
        try {
          console.log('🎯 Total revenue query detected, fetching comprehensive data...');
          
          // Fetch campaign data for total revenue calculation
          const campaignIntent = { ...intent, platform: 'all', timeframe: '30' };
          const campaignData = await fetchCampaignData(campaignIntent);
          
          if (!campaignData.success || !campaignData.data) {
            return 'Üzgünüm, toplam gelir verileri alınamadı. Lütfen brand bağlantılarınızı kontrol edin.';
          }
          
          // Calculate totals from all sources
          const { summary } = campaignData;
          const totalRevenue = (summary.google_ads_summary?.total_conversion_value || 0) + 
                              (summary.meta_ads_summary?.total_conversion_value || 0) + 
                              (summary.ga4_summary?.total_revenue || 0);
          
          const totalSpend = (summary.google_ads_summary?.total_cost || 0) + 
                            (summary.meta_ads_summary?.total_cost || 0);
          
          const overallROAS = totalSpend > 0 ? (totalRevenue / totalSpend) : 0;
          
          // Dil algılama
          const userMessage = lastUserMessage.content.toLowerCase();
          const hasTurkishChars = /[çğıöşüÇĞIİÖŞÜ]/.test(lastUserMessage.content);
          const hasTurkishWords = ['kampanya', 'performans', 'gelir', 'harcama', 'toplam', 'bütün', 'tüm', 'genel'].some(word => userMessage.includes(word));
          const hasEnglishWords = ['total', 'revenue', 'overall', 'all', 'channels'].some(word => userMessage.includes(word));
          const userLanguage = (hasEnglishWords && !hasTurkishChars && !hasTurkishWords) ? 'en' : 'tr';
          
          let enrichedSystemMessage = userLanguage === 'tr' ? 
            `${personalizedPrefix}Kullanıcı tüm kanallardan gelen toplam geliri sordu. İşte gerçek verilerden hesaplanmış özet:

**Toplam Gelir Analizi (${summary.date_range.days} günlük):**
• 📊 **Google Ads Geliri:** ₺${(summary.google_ads_summary?.total_conversion_value || 0).toLocaleString()}
• 📱 **Meta Ads Geliri:** ₺${(summary.meta_ads_summary?.total_conversion_value || 0).toLocaleString()}  
• 📈 **GA4 Tracked Gelir:** ₺${(summary.ga4_summary?.total_revenue || 0).toLocaleString()}
• 💰 **Toplam Harcama:** ₺${totalSpend.toLocaleString()}
• 📈 **Genel ROAS:** ${overallROAS.toFixed(2)}

Bu verileri analiz et ve kullanıcıya anlayabilecekleri şekilde açıkla. Trend analizi yap, öneriler sun ve sorularını yanıtla.` :
            `${personalizedPrefix}The user asked about total revenue from all channels. Here's the real data summary:

**Total Revenue Analysis (${summary.date_range.days} days):**
• 📊 **Google Ads Revenue:** ₺${(summary.google_ads_summary?.total_conversion_value || 0).toLocaleString()}
• 📱 **Meta Ads Revenue:** ₺${(summary.meta_ads_summary?.total_conversion_value || 0).toLocaleString()}
• 📈 **GA4 Tracked Revenue:** ₺${(summary.ga4_summary?.total_revenue || 0).toLocaleString()}
• 💰 **Total Spend:** ₺${totalSpend.toLocaleString()}
• 📈 **Overall ROAS:** ${overallROAS.toFixed(2)}

Analyze this data and explain it to the user in an understandable way. Provide trend analysis, recommendations, and answer their questions.`;

          // Hafıza özetini prompt'a ekle
          enrichedSystemMessage = appendMemoryToPrompt(enrichedSystemMessage, userMemory);

          const formattedMessages = [
            { role: 'system', content: enrichedSystemMessage },
            { role: 'user', content: lastUserMessage.content }
          ];

          const content = await callGlowyAI(formattedMessages, { narrative: true });
          return content;

        } catch (dataError) {
          console.error('❌ Error fetching total revenue data:', dataError);
          // Fallback to normal AI response
        }
      }
      
      // UNIFIED CHANNEL PERFORMANCE HANDLER (including revenue queries routed from analytics)
      else if (intent.type === 'channel_performance' && intent.confidence > 0.8) {
        try {
          console.log(`🎯 Channel performance query detected. Channel: ${intent.channel || 'All'}`);
          const channelData = await fetchChannelMonthlyRevenue(intent);
          const analysisObject = analyzeChannelMonthlyRevenue(channelData, intent);

          if (!analysisObject) {
            return `Üzgünüm, istenen kriterlere uygun kanal performans verisi bulamadım.`;
          }

          // Dil algılama - kullanıcının son mesajına göre
          const userMessage = lastUserMessage.content.toLowerCase();
          const hasTurkishChars = /[çğıöşüÇĞIİÖŞÜ]/.test(lastUserMessage.content);
          const hasTurkishWords = ['kampanya', 'performans', 'gelir', 'harcama', 'roas', 'tıklama', 'oturum', 'dönüşüm', 'aylık', 'günlük', 'haftalık', 'maliyet', 'cpc', 'kırılım', 'trafik', 'analiz', 'son', 'geçen', 'ay', 'hafta', 'gün'].some(word => userMessage.includes(word));
          const hasEnglishWords = ['performance', 'campaign', 'revenue', 'spend', 'click', 'session', 'conversion', 'last', 'month', 'week', 'day', 'show', 'compare', 'analyze'].some(word => userMessage.includes(word));
          
          // Eğer sadece İnglizce kelimeler varsa ve Türkçe yok ise İnglizce
          const userLanguage = (hasEnglishWords && !hasTurkishChars && !hasTurkishWords) ? 'en' : 'tr';
          
          let systemMessage = userLanguage === 'tr' ? 
            `${personalizedPrefix}Sen GlowyAI'sın, uzman bir pazarlama analisti. Görevin, sağlanan JSON verisini analiz edip kullanıcıya yardımcı, konuşkan ve anlayışlı bir şekilde sunmak.

**VERİ (JSON formatında):**
\`\`\`json
${JSON.stringify(analysisObject, null, 2)}
\`\`\`

**KRİTİK TALİMATLAR:**
1.  **Konuşkan Ol:** Neyi gösterdiğini onaylayarak başla (örneğin, "İşte '${analysisObject.query.channel}' kanalının son ${analysisObject.query.timeframeInMonths} aylık performans detayı...").
2.  **Analiz Et, Sadece Listeme:** Her ay için veriyi okunabilir paragraflar veya net madde işaretleri ile sun.
3.  **Projeksiyonları İşle (En Önemli!):** \`isCurrentMonth: true\` olan bir ay görürsen, o verisi eksik. Analizinde \`projectedRevenue\` ve \`projectedSessions\` alanlarını MUTLAKA kullan. "Performans düştü" demek yerine, "Temmuz'da şu ana kadar X, aya sonuna kadar projeksiyon Y'ye ulaşarak önceki ayları geçebilir" gibi ifadeler kullan.
4.  **Önemli Trend ve İçgörüleri Vurgula:** Verileri sunduktan sonra özet ver. Mevcut ay projeksiyonlarını dikkate alarak en önemli trendleri belirt.
5.  **Uygulanabilir Öneriler Sun:** SADECE sağlanan veriye dayanarak 1-2 somut, uygulanabilir öneri ver.
6.  **Mutlak Veri Bağlılığı:** JSON'daki sayılara MUTLAKA sadık kal. Veri icat etme, varsayma veya hallüsinasyon yapma. Bir metrik yoksa (örneğin 'Organic' için 'cost' tanımsızsa), bahsetme.
7.  **Formatlama:** Başlıklar ve vurgu için kalın metin (\`**...**\`) kullan. Markdown başlıkları kullanma (# ## ### gibi).
 8.  **FORMAT KURALLARI:** Cevaplarını kısa ve öz tut. Çok fazla emoji kullanma (max 3-4). Uzun listeler yapmak yerine önemli noktaları özetle. Professional bir ton kullan.` :
            `${personalizedPrefix}You are GlowyAI, an expert marketing analyst. Your task is to analyze the provided JSON data and present it to the user in a helpful, conversational, and insightful way.

**DATA (in JSON format):**
\`\`\`json
${JSON.stringify(analysisObject, null, 2)}
\`\`\`

**CRITICAL INSTRUCTIONS:**
1.  **Be Conversational:** Start by confirming what you're showing (e.g., "Here's the performance breakdown for the '${analysisObject.query.channel}' channel over the last ${analysisObject.query.timeframeInMonths} months...").
2.  **Analyze, Don't Just List:** For each month, present the data in a readable paragraph or clear bullet points.
3.  **Handle Projections (Most Important!):** If you see a month with \`isCurrentMonth: true\`, its data is incomplete. You MUST use the \`projectedRevenue\` and \`projectedSessions\` fields for your analysis. Instead of saying "performance dropped," you should say something like, "July's performance so far is X, and it's on track to reach a projected Y by the end of the month, potentially surpassing previous months."
4.  **Highlight Key Trends & Insights:** After presenting the data, provide a summary. Point out the most important trends, considering the projections for the current month.
5.  **Provide Actionable Recommendations:** Based ONLY on the provided data, give 1-2 concrete, actionable recommendations.
6.  **Absolute Data Fidelity:** You MUST be faithful to the numbers in the JSON. Do NOT invent, assume, or hallucinate any data. If a metric is not present (e.g., 'cost' is undefined for 'Organic'), do not mention it.
7.  **Formatting:** Use bold text (\`**...**\`) for titles and emphasis. Do not use markdown headers (like #, ##, ###).
8.  **FORMAT RULES:** Keep responses concise and to the point. Don't use too many emojis (max 3-4). Instead of long lists, summarize key points. Use a professional tone.`;

          const formattedMessages = [
            { role: 'system', content: systemMessage },
            { role: 'user', content: lastUserMessage.content }
          ];

          const content = await callGlowyAI(formattedMessages, { narrative: true });
          return content;

        } catch (dataError) {
          console.error('❌ Error fetching channel performance data:', dataError);
          return 'Sorry, I encountered an error while analyzing channel performance.';
        }
      }
      // PAID vs NON-PAID SPLIT HANDLER
      else if (intent.type === 'paid_nonpaid' && intent.confidence > 0.7) {
        try {
          console.log('🎯 Paid vs Non-Paid split detected, fetching analytics and ads data...');
          // Reuse existing endpoints: GA traffic source for non-paid, campaign data for paid
          const brandId = localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId');
          const timeframe = intent.timeframe || '30';
          const params = new URLSearchParams();
          params.append('dateRange', timeframe);
          // GA4 traffic (non-paid side) – we will call backend via existing endpoint from SourceMediumMonthly page
          const gaResponse = await fetch(`https://api.glowytics.com/api/google-ads/ga-traffic-source?${params.toString()}`, {
            headers: { 'X-Brand-ID': brandId },
            credentials: 'include'
          });
          const gaData = gaResponse.ok ? await gaResponse.json() : { detailedData: [] };

          // Paid side: use campaign-performance unified endpoint
          const paidIntent = { platform: 'all', timeframe };
          const paidData = await fetchCampaignData(paidIntent);

          // Aggregate quick summary
          const nonPaidSessions = (gaData?.summaryBySourceMedium || []).filter(r => {
            const m = (r.medium || '').toLowerCase();
            return !['cpc','paid','ppc','social-cpc','social_cpc'].some(x => m.includes(x));
          }).reduce((s, r) => s + Number(r.sessions || 0), 0);
          const nonPaidRevenue = (gaData?.summaryBySourceMedium || []).filter(r => {
            const m = (r.medium || '').toLowerCase();
            return !['cpc','paid','ppc','social-cpc','social_cpc'].some(x => m.includes(x));
          }).reduce((s, r) => s + Number(r.totalRevenue || 0), 0);

          const paidRevenue = Number(paidData?.summary?.google_ads_summary?.total_conversion_value || 0) + Number(paidData?.summary?.meta_ads_summary?.total_conversion_value || 0);
          const paidCost = Number(paidData?.summary?.google_ads_summary?.total_cost || 0) + Number(paidData?.summary?.meta_ads_summary?.total_cost || 0);
          const paidROAS = paidCost > 0 ? (paidRevenue / paidCost) : 0;

          const userMessage = lastUserMessage.content.toLowerCase();
          const hasTurkishChars = /[çğıöşüÇĞIİÖŞÜ]/.test(lastUserMessage.content);
          const hasTurkishWords = ['kampanya','performans','gelir','harcama','roas','oturum','aylık','günlük','paid','non','organik','organic'].some(w => userMessage.includes(w));
          const hasEnglishWords = ['paid','non-paid','organic','sessions','revenue','roas'].some(w => userMessage.includes(w));
          const userLanguage = (hasEnglishWords && !hasTurkishChars && !hasTurkishWords) ? 'en' : 'tr';

          let text = '';
          if (userLanguage === 'tr') {
            text += `${personalizedPrefix}**Paid vs Non‑Paid Performans Özeti (${timeframe} gün)**\n`;
            text += `• **Paid (Ads)**: Gelir ₺${paidRevenue.toLocaleString('tr-TR', {maximumFractionDigits:0})}, Harcama ₺${paidCost.toLocaleString('tr-TR', {maximumFractionDigits:0})}, ROAS ${paidROAS.toFixed(2)}\n`;
            text += `• **Non‑Paid (Organic/Direct/Referral/Email)**: Gelir ₺${nonPaidRevenue.toLocaleString('tr-TR', {maximumFractionDigits:0})}, Oturum ${nonPaidSessions.toLocaleString('tr-TR')}\n`;
            text += `\n**Öneri**\n`;
            text += `- Paid tarafta ROAS ${paidROAS.toFixed(2)}. ROAS>4 olan kampanyalara bütçe artırımı, <2 olanlara optimizasyon önerilir.\n`;
            text += `- Non‑Paid tarafta en yüksek sesi getiren source/medium’ları (organic, direct, referral) güçlendirmek için içerik/SEO planı önerilir.`;
          } else {
            text += `${personalizedPrefix}**Paid vs Non‑Paid Performance (${timeframe} days)**\n`;
            text += `• **Paid (Ads)**: Revenue ₺${paidRevenue.toLocaleString('tr-TR', {maximumFractionDigits:0})}, Spend ₺${paidCost.toLocaleString('tr-TR', {maximumFractionDigits:0})}, ROAS ${paidROAS.toFixed(2)}\n`;
            text += `• **Non‑Paid (Organic/Direct/Referral/Email)**: Revenue ₺${nonPaidRevenue.toLocaleString('tr-TR', {maximumFractionDigits:0})}, Sessions ${nonPaidSessions.toLocaleString('tr-TR')}\n`;
            text += `\n**Suggestions**\n`;
            text += `- If ROAS>${paidROAS.toFixed(2)} is strong, scale top campaigns; optimize those <2.\n`;
            text += `- Strengthen organic channels (content/SEO) that drive most sessions.`;
          }

          // Öğrenmeyi tetikle
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return text;
        } catch (e) {
          console.error('❌ Paid vs Non-Paid handler error:', e);
        }
      }
      
      // 🎯 ROAS Trend Analysis (5+4 Day Methodology)
      else if (intent.type === 'roas_trend_analysis' && intent.confidence > 0.8) {
        try {
          console.log('🎯 ROAS Trend Analysis (5+4) query detected, fetching 9 days of data...');
          
          // Force 9 days for the 5+4 methodology
          const roasIntent = { ...intent, timeframe: '9' };
          const campaignData = await fetchCampaignData(roasIntent);
          const analysis = analyzeROASTrendFor5Plus4Days(campaignData, roasIntent);
          
          // Return the analysis directly without OpenAI processing for faster response
          // Öğrenmeyi tetikle (erken dönüşten önce)
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return personalizedPrefix + analysis;
          
        } catch (dataError) {
          console.error('❌ Error in ROAS trend analysis:', dataError);
          return '❌ ROAS trend analizi yapılırken bir hata oluştu. Lütfen brand bağlantılarınızı kontrol edin ve tekrar deneyin.';
        }
      }
      else if (intent.type === 'budget_optimization_suggestions' && intent.confidence > 0.8) {
        try {
          console.log('🎯 Budget Optimization Suggestions detected (5+4 rule).');
          const roasIntent = { ...intent, timeframe: '9' };
          const campaignData = await fetchCampaignData(roasIntent);
          if (!campaignData?.success) {
            return 'Üzgünüm, bütçe önerileri için gerekli veriler alınamadı.';
          }

          // Build daily data per campaign (Google + Meta), then compute 5+4 averages
          const allCampaigns = [];
          const addRows = (rows, platformLabel) => {
            (rows || []).forEach(row => {
              allCampaigns.push({
                platform: platformLabel,
                campaign_id: row.campaign_id,
                campaign_name: row.campaign_name || row.name || row.title || `Campaign_${row.campaign_id}`,
                date: row.date,
                cost: Number(row.total_cost || 0),
                conversion_value: Number(row.total_conversion_value || 0)
              });
            });
          };
          addRows(campaignData.data?.google_ads, 'google_ads');
          addRows(campaignData.data?.meta_ads, 'meta_ads');

          const campaignGroups = {};
          allCampaigns.forEach(c => {
            const key = `${c.platform}_${c.campaign_id}`;
            if (!campaignGroups[key]) campaignGroups[key] = { platform: c.platform, campaign_id: c.campaign_id, campaign_name: c.campaign_name, daily: [] };
            const roas = c.cost > 0 ? c.conversion_value / c.cost : 0;
            campaignGroups[key].daily.push({ date: c.date, roas, cost: c.cost });
          });

          const recommendations = [];
          Object.values(campaignGroups).forEach(g => {
            g.daily.sort((a,b)=> new Date(a.date) - new Date(b.date));
            if (g.daily.length < 6) return;
            const first5 = g.daily.slice(0,5);
            const last4 = g.daily.slice(-4);
            const avg = arr => arr.reduce((s,d)=> s + d.roas, 0) / arr.length;
            const avg5 = avg(first5);
            const avg4 = avg(last4);
            const change = avg4 - avg5;
            const changePct = avg5 > 0 ? (change/avg5)*100 : (avg4>0 ? 100 : 0);
            const type = avg4 > avg5 ? 'INCREASE' : 'DECREASE';
            recommendations.push({
              platform: g.platform,
              campaign_name: g.campaign_name,
              avg5: Number(avg5.toFixed(2)),
              avg4: Number(avg4.toFixed(2)),
              change: Number(change.toFixed(2)),
              changePct: Number(changePct.toFixed(1)),
              recommendationType: type
            });
          });

          // Sort by absolute improvement/deterioration
          recommendations.sort((a,b)=> Math.abs(b.change) - Math.abs(a.change));
          const increaseList = recommendations.filter(r=> r.recommendationType==='INCREASE').slice(0,5);
          const decreaseList = recommendations.filter(r=> r.recommendationType==='DECREASE').slice(0,5);

          // Fetch current budgets for Google Ads campaigns to produce absolute TRY suggestions
          const brandId = localStorage.getItem('selectedBrandId') || localStorage.getItem('currentBrandId');
          const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const end = new Date();
          const start = new Date();
          start.setDate(end.getDate() - 9 + 1);
          let budgetMap = {};
          try {
            const budgetUrl = `https://api.glowytics.com/api/google-ads/unified-googleads-ga4-performance?dateRange=custom&startDate=${fmt(start)}&endDate=${fmt(end)}`;
            const budRes = await fetch(budgetUrl, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
            const budJson = budRes.ok ? await budRes.json() : [];
            // Use latest entry per campaign_name with positive budget_amount
            (budJson || []).forEach(row => {
              const nameKey = (row.campaign_name || '').toLowerCase();
              const amt = Number(row.budget_amount || 0);
              if (!nameKey || amt <= 0) return;
              // Prefer latest date
              const prev = budgetMap[nameKey];
              if (!prev || new Date(row.date) > new Date(prev.date)) {
                budgetMap[nameKey] = { amount: amt, date: row.date };
              }
            });
          } catch (e) {
            console.warn('Budget map fetch failed, proceeding without absolute amounts', e?.message);
          }

          // Create tasks via dynamic endpoint
          const createdTasks = [];
          const createTask = async (rec) => {
            const priority = Math.abs(rec.changePct) >= 30 ? 'high' : 'medium';
            let title = rec.recommendationType === 'INCREASE' ? `Budget Increase: ${rec.campaign_name}` : `Budget Decrease: ${rec.campaign_name}`;
            let description = `5+4 ROAS method. First5 Avg: ${rec.avg5}, Last4 Avg: ${rec.avg4}, Δ ${rec.change.toFixed(2)} (${rec.changePct}%). Recommendation: ${rec.recommendationType}.`;

            // If Google Ads and we have current budget, compute absolute suggestion
            if (rec.platform === 'google_ads') {
              const key = (rec.campaign_name || '').toLowerCase();
              const current = budgetMap[key]?.amount;
              if (current && current > 0) {
                const suggested = Math.round(current * (1 + (rec.changePct / 100)));
                const curTxt = current.toLocaleString('tr-TR');
                const sugTxt = suggested.toLocaleString('tr-TR');
                title = `${title} (₺${curTxt} → ₺${sugTxt})`;
                description += `\nSuggested absolute budget: ₺${curTxt} → ₺${sugTxt}`;
              }
            }
            const payload = {
              brandId,
              title,
              description,
              category: 'budget_optimization',
              priority,
              platform: rec.platform,
              estimatedTime: 'PT10M',
              source: 'ai_chat_5plus4'
            };
            try {
              const res = await axios.post('https://api.glowytics.com/api/ai-suggestions/generate-dynamic', payload, { withCredentials: true, headers: { 'X-Brand-ID': brandId } });
              if (res.data?.success) createdTasks.push(res.data?.suggestion?.id || 'ok');
            } catch (e) {
              console.warn('Task creation failed for', title, e?.message);
            }
          };
          await Promise.all([...increaseList, ...decreaseList].map(createTask));

          // Build response text
          let text = `${personalizedPrefix}**5+4 Metoduna Göre Bütçe Önerileri**\n`;
          if (increaseList.length) {
            text += `\n**Artır (Top ${increaseList.length})**\n`;
            increaseList.forEach((r,i)=>{ text += `${i+1}. ${r.campaign_name} → Avg5 ${r.avg5} → Avg4 ${r.avg4} (Δ ${r.change.toFixed(2)} | ${r.changePct}%)\n`; });
          }
          if (decreaseList.length) {
            text += `\n**Azalt (Top ${decreaseList.length})**\n`;
            decreaseList.forEach((r,i)=>{ text += `${i+1}. ${r.campaign_name} → Avg5 ${r.avg5} → Avg4 ${r.avg4} (Δ ${r.change.toFixed(2)} | ${r.changePct}%)\n`; });
          }
          if (createdTasks.length) {
            text += `\n✅ ${createdTasks.length} adet AI görev task olarak oluşturuldu (Task Board > type: ai).`;
          } else {
            text += `\nℹ️ Task oluşturma denendi; uygun kampanya bulunamadıysa hiç task açılmamış olabilir.`;
          }

          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return text;
        } catch (e) {
          console.error('❌ Budget optimization suggestions error:', e);
          return '❌ Bütçe önerileri oluşturulurken bir hata oluştu.';
        }
      }
      // BUDGET REALLOCATION (7+2) HANDLER
      else if (intent.type === 'budget_reallocation_7plus2' && intent.confidence > 0.8) {
        try {
          console.log('🎯 Budget Reallocation (7+2) detected, fetching 9 days detailed data...');
          // Use detailed Google Ads view with date_campaign grouping to compute 7+2
          const detailedIntent = { ...intent, timeframe: '9', platform: 'google_ads', groupBy: 'date_campaign' };
          const campaignData = await fetchGoogleAdsDetailed(detailedIntent);
          if (!campaignData?.success) {
            return 'Üzgünüm, 7+2 analizi için gerekli veriler alınamadı.';
          }
          // Reuse existing analyzer for 5+4 but label as 7+2; we can adapt text
          const analysis = analyzeROASTrendFor5Plus4Days(campaignData, detailedIntent)
            .replaceAll('5+4', '7+2')
            .replaceAll('9 günlük', '9 günlük (7+2)');

          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return personalizedPrefix + analysis;
        } catch (e) {
          console.error('❌ Budget Reallocation (7+2) error:', e);
          return '❌ 7+2 bütçe yeniden dağıtımı analizi sırasında bir hata oluştu.';
        }
      }
      
      // If campaign performance question (but NOT revenue-routed analytics), fetch real data
      else if (intent.type === 'campaign_performance' && intent.confidence > 0.6 && intent.dataSource !== 'analytics') {
        try {
          console.log('🎯 Campaign performance query detected, fetching real data...');
          
          let analysis;
          
          // For Google Ads queries, use the detailed view, but only if a specific groupBy is requested
          // EXCEPT for campaign listing which needs multi-platform data
          if (intent.platform === 'google_ads' || 
              (intent.platform === 'all' && intent.groupBy && intent.groupBy !== 'date' && intent.groupBy !== 'campaign_name')) {
            console.log('🔍 Using detailed Google Ads view for specific groupBy analysis...');
            const detailedData = await fetchGoogleAdsDetailed(intent);
            analysis = analyzeGoogleAdsDetailed(detailedData, intent);
          } else {
            // For general performance queries, Meta Ads, campaign listing, or when no specific groupBy is needed
            console.log('📊 Using standard campaign data for general overview...');
            const campaignData = await fetchCampaignData(intent);
            analysis = analyzeCampaignData(campaignData, intent);
          }
          
          // Gerçek veri analizini direkt olarak döndür, AI'a tekrar yorumlatma
          // Öğrenmeyi tetikle
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return personalizedPrefix + (analysis || '');
        } catch (dataError) {
          console.error('❌ Error fetching campaign data:', dataError);
          // Fallback to normal AI response
        }
      }
      // Eğer campaign performance sorusu ise, gerçek veriyi çek
      else if (intent.type === 'channel_monthly_revenue' && intent.confidence > 0.7) {
        try {
          console.log('🎯 Channel revenue query detected, fetching real data...');
          const channelData = await fetchChannelMonthlyRevenue(intent);
          const analysis = analyzeChannelMonthlyRevenue(channelData, intent);

          const enrichedSystemMessage = `You are GlowyAI. The user asked about channel performance. I have analyzed their real campaign data and here is the analysis:

${analysis}

Please provide additional insights, explanations, and actionable recommendations based on this data. Be conversational and helpful. You can reference the specific numbers and suggest next steps for optimization.`;

          const formattedMessages = [
            { role: 'system', content: enrichedSystemMessage },
            { role: 'user', content: lastUserMessage.content }
          ];

          const content = await callGlowyAI(formattedMessages, { narrative: true });
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return `${personalizedPrefix}${analysis}\n\n---\n\n${content}`;

        } catch (dataError) {
          console.error('❌ Error fetching channel revenue data:', dataError);
        }
      }
      else if (intent.type === 'product_performance' && intent.confidence > 0.7) {
        try {
          console.log('🎯 Product performance query detected, fetching real data...');
          const productData = await fetchProductPerformanceAnalysis(intent);
          intent.originalQuery = lastUserMessage.content;
          const analysis = analyzeProductPerformance(productData, intent);

          let enrichedSystemMessage;
          
          if (intent.metric) {
            enrichedSystemMessage = `The user asked for a specific metric for a brand. The calculated answer is: "${analysis}". 
Present this answer directly and concisely. Do not explain the formula. If the data for the specific brand couldn't be found, state that clearly.
After providing the direct answer, you can offer to show the full list or help with something else.`;
          } else if(intent.brandName) {
            enrichedSystemMessage = `The user asked for the performance of a specific brand. The summary is: "${analysis}". 
            Present this summary directly. Do not show a list of other brands. Start your response with something like "Of course! Here is the performance for [Brand Name]...".`;
          } else {
            enrichedSystemMessage = `You are GlowyAI. The user asked about product performance. I have analyzed their real e-commerce data and here is the analysis based on their query type (${intent.analysisType}):

${analysis}

Please provide additional insights, explanations, and actionable recommendations based on this data. Be conversational and helpful.`;
          }

          const formattedMessages = [
            { role: 'system', content: enrichedSystemMessage },
            { role: 'user', content: lastUserMessage.content }
          ];

          const content = await callGlowyAI(formattedMessages, { narrative: true });
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return `${personalizedPrefix}${analysis}\n\n---\n\n${content}`;

        } catch (dataError) {
          console.error('❌ Error fetching product performance data:', dataError);
        }
      }
      // COMPARE GOOGLE vs META HANDLER (quick KPIs)
      else if (intent.type === 'compare_google_meta' && intent.confidence > 0.85) {
        try {
          const brandId = localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId');
          const days = parseInt(intent.timeframe || '30', 10);
          const end = new Date();
          const start = new Date();
          start.setDate(end.getDate() - days + 1);
          const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

          // Use unified campaign view to get cost/conversions/revenue per channel
          const url = `https://api.glowytics.com/api/google-ads/unified-campaign-performance?dateRange=custom&startDate=${fmt(start)}&endDate=${fmt(end)}`;
          const res = await fetch(url, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
          const json = res.ok ? await res.json() : [];
          const rows = Array.isArray(json) ? json : [];

          const agg = { google: { rev:0, cost:0 }, meta: { rev:0, cost:0 } };
          rows.forEach(r => {
            const rev = Number(r.ga4_total_revenue || 0);
            if (r.source === 'google') { agg.google.rev += rev; agg.google.cost += Number(r.google_cost || 0); }
            if (r.source === 'meta') { agg.meta.rev += rev; agg.meta.cost += Number(r.meta_cost || 0); }
          });
          const roas = (rev,cost)=> cost>0? (rev/cost):0;
          const gRoas = roas(agg.google.rev, agg.google.cost);
          const mRoas = roas(agg.meta.rev, agg.meta.cost);

          let text = `${personalizedPrefix}**Google vs Meta (${days} gün)**\n`;
          text += `• Google: Revenue ₺${Math.round(agg.google.rev).toLocaleString('tr-TR')}, Cost ₺${Math.round(agg.google.cost).toLocaleString('tr-TR')}, ROAS ${gRoas.toFixed(2)}\n`;
          text += `• Meta: Revenue ₺${Math.round(agg.meta.rev).toLocaleString('tr-TR')}, Cost ₺${Math.round(agg.meta.cost).toLocaleString('tr-TR')}, ROAS ${mRoas.toFixed(2)}\n`;
          text += gRoas>mRoas ? `→ Bu dönemde ROAS açısından Google önde.` : gRoas<mRoas ? `→ Bu dönemde ROAS açısından Meta önde.` : `→ ROAS değerleri benzer.`;
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return text;
        } catch (e) {
          console.error('❌ compare_google_meta error:', e);
        }
      }

      // ANOMALY OVERVIEW HANDLER (sessions/revenue spikes)
      else if (intent.type === 'anomaly_overview' && intent.confidence > 0.8) {
        try {
          const brandId = localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId');
          const days = 30; // analyze last 30 days
          const end = new Date();
          const start = new Date();
          start.setDate(end.getDate() - days + 1);
          const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const url = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=custom&startDate=${fmt(start)}&endDate=${fmt(end)}`;
          const res = await fetch(url, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
          const json = res.ok ? await res.json() : { detailedData: [] };
          const byDate = new Map();
          (json?.detailedData||[]).forEach(r=>{
            const k = r.date; if (!k) return;
            const d = byDate.get(k) || { sessions:0, revenue:0 };
            d.sessions += Number(r.sessions||0);
            d.revenue += Number(r.totalRevenue||0);
            byDate.set(k,d);
          });
          const series = Array.from(byDate.entries()).sort((a,b)=> new Date(a[0]) - new Date(b[0])).map(([date,v])=>({date, ...v}));
          const last7 = series.slice(-7); const prev7 = series.slice(-14,-7);
          const sum = (arr,k)=> arr.reduce((s,x)=> s + Number(x[k]||0), 0);
          const lastSes = sum(last7,'sessions'); const prevSes = sum(prev7,'sessions');
          const lastRev = sum(last7,'revenue'); const prevRev = sum(prev7,'revenue');
          const sesChange = prevSes>0? ((lastSes-prevSes)/prevSes*100): null;
          const revChange = prevRev>0? ((lastRev-prevRev)/prevRev*100): null;
          let text = `${personalizedPrefix}**Son 7 güne göre anomaliler**\n`;
          text += `• Sessions: ${lastSes.toLocaleString('tr-TR')} (${sesChange===null?'N/A':sesChange.toFixed(1)+'%'})\n`;
          text += `• Revenue: ₺${Math.round(lastRev).toLocaleString('tr-TR')} (${revChange===null?'N/A':revChange.toFixed(1)+'%'})\n`;
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return text;
        } catch(e){ console.error('❌ anomaly_overview error', e); }
      }

      // THRESHOLD FILTER HANDLER (simple expression search like ROAS<2, spend>1000)
      else if (intent.type === 'filter_campaigns_by_thresholds' && intent.confidence > 0.8) {
        try {
          const platform = intent.platform || 'google_ads';
          const data = await fetchGoogleAdsDetailed({ groupBy:'campaign_name', timeframe: intent.timeframe || '30', platform });
          const rows = Array.isArray(data?.data) ? data.data : [];
          // Parse thresholds (basic): ROAS<2, cost>1000
          const msg = (messages[messages.length-1]?.content||'').toLowerCase().replace(',', '.');
          const roasMatch = msg.match(/roas\s*([<>]=?)\s*(\d+(?:[\.,]\d+)?)/);
          const costMatch = msg.match(/(harcama|cost|spend)\s*([<>]=?)\s*(\d+(?:[\.,]\d+)?)/);
          const norm = (s)=> Number(String(s).replace(',','.'));
          const cmp = (a,op,b)=> op==='>='? a>=b: op==='>'? a>b: op==='<'? a<b: op==='<= '? a<=b : a<b;
          const filtered = rows.filter(r=>{
            const cost = Number(r.total_cost||0);
            const rev = Number(r.total_revenue||r.total_conversion_value||0);
            const roas = cost>0? rev/cost: 0;
            let ok = true;
            if (roasMatch){ ok = ok && cmp(roas, roasMatch[1]||'<', norm(roasMatch[2])); }
            if (costMatch){ ok = ok && cmp(cost, costMatch[2]||'>', norm(costMatch[3])); }
            return ok;
          });
          filtered.sort((a,b)=> Number(b.total_revenue||b.total_conversion_value||0) - Number(a.total_revenue||a.total_conversion_value||0));
          const top = filtered.slice(0, 30).map((r,i)=> {
            const rev = Number(r.total_revenue||r.total_conversion_value||0);
            const cost = Number(r.total_cost||0);
            const roas = cost>0? rev/cost : 0;
            return `${i+1}. ${r.campaign_name} — Rev ₺${Math.round(rev).toLocaleString('tr-TR')}, Cost ₺${Math.round(cost).toLocaleString('tr-TR')}, ROAS ${roas.toFixed(2)}`;
          }).join('\n');
          const text = `${personalizedPrefix}**Filtreli Kampanyalar (son ${intent.timeframe||'30'} gün)**\n${top || 'Kriterlere uyan kampanya bulunamadı.'}`;
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return text;
        } catch(e){ console.error('❌ filter_campaigns_by_thresholds error', e); }
      }

      // METRIC DEFINITION HANDLER
      else if (intent.type === 'metric_definition' && intent.confidence > 0.99) {
        return personalizedPrefix + intent.definitionText;
      }
      // TOP CAMPAIGNS HANDLER
      else if (intent.type === 'top_campaigns' && intent.confidence > 0.85) {
        try {
          const brandId = localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId');
          const n = intent.topN || 30;
          const timeframe = intent.timeframe || '30';
          const params = new URLSearchParams({ groupBy: 'campaign_name' });
          if (timeframe) params.append('dateRange', timeframe);

          const fetchPlatform = async (platform) => {
            const platformIntent = { groupBy: 'campaign_name', timeframe, platform };
            const data = await fetchGoogleAdsDetailed(platformIntent);
            const rows = Array.isArray(data?.data) ? data.data : [];
            // Aggregate last 30 days by campaign name (sum revenue, sum cost)
            const totals = new Map();
            rows.forEach(r => {
              const name = (r.campaign_name || r.name || r.title || 'Unknown').trim();
              const key = name.toLowerCase();
              const revenue = Number(r.total_revenue || r.total_conversion_value || 0);
              const cost = Number(r.total_cost || 0);
              const prev = totals.get(key) || { name, revenue: 0, cost: 0 };
              prev.revenue += revenue;
              prev.cost += cost;
              totals.set(key, prev);
            });
            const scored = Array.from(totals.values()).map(x => ({
              name: x.name,
              revenue: x.revenue,
              cost: x.cost,
              roas: x.cost > 0 ? x.revenue / x.cost : 0
            }));
            scored.sort((a,b)=> (b.revenue||0) - (a.revenue||0));
            return scored.slice(0, n);
          };

          let text = personalizedPrefix;
          if (intent.platform === 'google_ads') {
            const topG = await fetchPlatform('google_ads');
            text += `**Google Ads - En İyi ${n} Kampanya (Gelire göre)**\n`;
            topG.forEach((r,i)=>{ text += `${i+1}. ${r.name} — Revenue ₺${Math.round(r.revenue).toLocaleString('tr-TR')}, ROAS ${r.roas.toFixed(2)}\n`; });
          } else if (intent.platform === 'meta_ads') {
            const topM = await fetchPlatform('meta_ads');
            text += `**Meta Ads - En İyi ${n} Kampanya (Gelire göre)**\n`;
            topM.forEach((r,i)=>{ text += `${i+1}. ${r.name} — Revenue ₺${Math.round(r.revenue).toLocaleString('tr-TR')}, ROAS ${r.roas.toFixed(2)}\n`; });
          } else {
            const [topG, topM] = await Promise.all([fetchPlatform('google_ads'), fetchPlatform('meta_ads')]);
            text += `**Google Ads - En İyi ${n}**\n`;
            topG.forEach((r,i)=>{ text += `${i+1}. ${r.name} — Revenue ₺${Math.round(r.revenue).toLocaleString('tr-TR')}, ROAS ${r.roas.toFixed(2)}\n`; });
            text += `\n**Meta Ads - En İyi ${n}**\n`;
            topM.forEach((r,i)=>{ text += `${i+1}. ${r.name} — Revenue ₺${Math.round(r.revenue).toLocaleString('tr-TR')}, ROAS ${r.roas.toFixed(2)}\n`; });
          }

          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return text;
        } catch (e) {
          console.error('❌ Top campaigns error:', e);
          return '❌ En iyi kampanyaları çekerken bir hata oluştu.';
        }
      }
      // ANALYTICS MONTHLY REVENUE BREAKDOWN HANDLER
      else if (intent.type === 'analytics_monthly_revenue_breakdown' && intent.confidence > 0.8) {
        try {
          console.log('🎯 Analytics monthly revenue breakdown detected');
          // Use GA traffic + unified ads revenue to build month-by-month total revenue if available
          const brandId = localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId');
          const days = parseInt(intent.timeframe || '180', 10);
          const end = new Date();
          const start = new Date();
          start.setDate(end.getDate() - days + 1);

          // Fetch GA4 traffic source (contains totalRevenue by date/source)
          const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const gaUrl = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=custom&startDate=${formatDate(start)}&endDate=${formatDate(end)}`;
          const gaRes = await fetch(gaUrl, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
          const gaJson = gaRes.ok ? await gaRes.json() : { detailedData: [] };

          // Aggregate revenue by month from GA detailedData
          const monthly = {};
          (gaJson?.detailedData || []).forEach(row => {
            const d = new Date(row.date);
            const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
            if (!monthly[key]) monthly[key] = { revenue: 0 };
            monthly[key].revenue += Number(row.totalRevenue || 0);
          });
          const sortedKeys = Object.keys(monthly).sort((a,b)=>a.localeCompare(b));

          let text = `${personalizedPrefix}**Aylık Ciro Kırılımı (${Math.round(days/30)} ay)**\n`;
          sortedKeys.forEach(k => {
            const [y,m] = k.split('-');
            const label = new Date(Number(y), Number(m)-1).toLocaleString('tr-TR', { month: 'long', year: 'numeric' });
            text += `• ${label}: ₺${Math.round(monthly[k].revenue).toLocaleString('tr-TR')}\n`;
          });

          // Öğrenmeyi tetikle
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return text;
        } catch (e) {
          console.error('❌ Analytics monthly revenue breakdown error:', e);
        }
      }
      else if (intent.type === 'analytics_monthly_organic_breakdown' && intent.confidence > 0.85) {
        try {
          console.log('🎯 Analytics monthly organic breakdown detected');
          const brandId = localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId');
          const days = parseInt(intent.timeframe || '180', 10);
          const end = new Date();
          const start = new Date();
          start.setDate(end.getDate() - days + 1);
          const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          const gaUrl = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=custom&startDate=${fmt(start)}&endDate=${fmt(end)}`;
          const gaRes = await fetch(gaUrl, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
          const gaJson = gaRes.ok ? await gaRes.json() : { detailedData: [] };

          const monthly = {};
          (gaJson?.detailedData || []).forEach(row => {
            const medium = (row.medium || row.medium_name || '').toLowerCase();
            if (!medium.includes('organic')) return; // only organic
            const d = new Date(row.date);
            const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
            if (!monthly[key]) monthly[key] = { sessions: 0, revenue: 0, transactions: 0 };
            monthly[key].sessions += Number(row.sessions || 0);
            monthly[key].revenue += Number(row.totalRevenue || 0);
            monthly[key].transactions += Number(row.eventCount || 0);
          });

          const keys = Object.keys(monthly).sort((a,b)=>a.localeCompare(b));
          let text = `${personalizedPrefix}**Aylık Organic Performans (${Math.round(days/30)} ay)**\n`;
          keys.forEach(k => {
            const [y,m] = k.split('-');
            const label = new Date(Number(y), Number(m)-1).toLocaleString('tr-TR', { month: 'long', year: 'numeric' });
            const sesVal = monthly[k].sessions;
            const txnVal = monthly[k].transactions;
            const cr = sesVal > 0 ? ((txnVal / sesVal) * 100).toFixed(2) : '0.00';
            const ses = sesVal.toLocaleString('tr-TR');
            const txn = txnVal.toLocaleString('tr-TR');
            const rev = Math.round(monthly[k].revenue).toLocaleString('tr-TR');
            text += `• ${label}: Sessions ${ses}, Transactions ${txn}, CR ${cr}%, Revenue ₺${rev}\n`;
          });

          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return text;
        } catch (e) {
          console.error('❌ Analytics monthly organic breakdown error:', e);
        }
      }
      else if (intent.type === 'single_channel_performance' && intent.confidence > 0.8) {
        try {
          console.log('🎯 Single channel query detected, fetching real data...');
          const channelData = await fetchChannelMonthlyRevenue(intent);
          const analysisObject = analyzeSingleChannelPerformance(channelData, intent);

          if (!analysisObject) {
            return `Üzgünüm, ${intent.channel} kanalı için istenen veriyi bulamadım.`;
          }

          // FINAL, REFINED PROMPT for balanced analysis
          const enrichedSystemMessage = `${personalizedPrefix}You are GlowyAI, an expert marketing analyst. Your task is to analyze the provided data and present it to the user in a helpful, conversational tone.

**DATA (in JSON format):**
\`\`\`json
${JSON.stringify(analysisObject, null, 2)}
\`\`\`

**CRITICAL INSTRUCTIONS:**
1.  **Analyze and Summarize:** Do not just list the data. Present it in a friendly, conversational way. Start by confirming what you're showing (e.g., "Here's the performance for the Direct channel over the last 4 months...").
2.  **Highlight Key Trends:** Mention the most important trends you see in the data. For example, point out if revenue or sessions are consistently increasing or decreasing.
3.  **Provide Actionable Insights:** Based ONLY on the provided data, give 1-2 concrete, actionable recommendations. For example, if sessions are high but purchases are low, suggest looking into conversion rate optimization.
4.  **Data Fidelity:** You MUST be faithful to the numbers in the JSON. Do NOT invent or hallucinate any data. If a metric is not present for a month (e.g., cost), do not mention it for that month.
5.  **Be Concise:** Keep your response clear and to the point.`;

          const formattedMessages = [
            { role: 'system', content: enrichedSystemMessage },
            { role: 'user', content: lastUserMessage.content }
          ];

          const content = await callGlowyAI(formattedMessages, { narrative: true });
          setTimeout(() => { learnFromConversation(messages, userId); }, 0);
          return content;

        } catch (dataError) {
          console.error('❌ Error fetching single channel data:', dataError);
          // Fallback to normal AI response
        }
      }
    }
    
    // Normal AI conversation flow (without real data)
    // Kullanıcı hafızasını al
    const userMemory = await getUserMemory(userId);
    
    // API isteği için tüm mesajları uygun formata dönüştürelim
    const formattedMessages = messages.map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      content: msg.content
    }));

    // Güncel tarih ve saati al
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      weekday: 'long'
    });
    const formattedTime = now.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });

    // Dil algılama - kullanıcının son mesajına göre
    const userMessage = lastUserMessage.content.toLowerCase();
    const hasTurkishChars = /[çğıöşüÇĞIİÖŞÜ]/.test(lastUserMessage.content);
    const hasTurkishWords = ['kampanya', 'performans', 'gelir', 'harcama', 'roas', 'tıklama', 'oturum', 'dönüşüm', 'aylık', 'günlük', 'haftalık', 'maliyet', 'cpc', 'kırılım', 'trafik', 'analiz', 'son', 'geçen', 'ay', 'hafta', 'gün'].some(word => userMessage.includes(word));
    const hasEnglishWords = ['performance', 'campaign', 'revenue', 'spend', 'click', 'session', 'conversion', 'last', 'month', 'week', 'day', 'show', 'compare', 'analyze'].some(word => userMessage.includes(word));
    
    // Eğer sadece İnglizce kelimeler varsa ve Türkçe yok ise İnglizce
    const userLanguage = (hasEnglishWords && !hasTurkishChars && !hasTurkishWords) ? 'en' : 'tr';
    
    // Kişiselleştirme: isim ve prefix (normal akış)
    const rememberedNameNormal = extractUserName(userMemory);
    const personalizedPrefix = buildPersonalizedPrefix(rememberedNameNormal, 'tr');

    // Sistem mesajını, kullanıcı hafızası ve güncel tarih içerecek şekilde güncelle
    let systemContent = userLanguage === 'tr' ? 
      `${personalizedPrefix}Sen GlowyAI, zeki ve proaktif bir pazarlama stratejistisin. Amacın sadece veri sunmak değil, bu verileri bir hikayeye dönüştürmek, kullanıcının hedeflerini anlamak ve onlara başarıya ulaşmaları için yol göstermek. Sen bir araç değil, kullanıcının başarısı için çalışan bir takım arkadaşısın.

**Kişilik ve Davranış Kuralları:**
1.  **İnsancıl ve Anlayışlı Ol:** Kullanıcının niyetini anlamaya çalış. "Anladım, yani X kampanyalarının genel karlılığını merak ediyorsun," gibi teyit cümleleri kur.
2.  **Proaktif Ol:** Verilerde önemli bir anomali, beklenmedik bir başarı veya bir risk fark edersen, kullanıcı sormadan bunu gündeme getir. Örneğin: "Bu kampanyanın ROAS'ı son 3 günde %40 düşmüş görünüyor, bir sorun olabilir. İncelemek ister misin?"
3.  **Stratejik Düşün:** Sadece geçmiş verileri raporlama. Geleceğe yönelik stratejik önerilerde bulun. "Bu verilere göre, bütçenin bir kısmını X kampanyasından Y kampanyasına kaydırmak daha karlı olabilir."
4.  **Sade ve Net Ol:** Karmaşık pazarlama jargonları yerine herkesin anlayabileceği bir dil kullan. Verileri görselleştirmek için basit metaforlar kullanmaktan çekinme.
5.  **Öğren ve Uyum Sağla:** Kullanıcının geçmiş sohbetlerinden ve tercihlerinden öğren. (Bu bilgi sana sağlanacak).

**Teknik Kurallar:**
*   **Veriye Sadık Kal:** ASLA hayali veri üretme. Eğer bir veri yoksa, "Bu bilgiye şu an erişemiyorum ama istersen X verisine bakabiliriz," gibi alternatifler sun.
*   **Formatlama:** Cevaplarını kısa ve öz tut. Çok fazla emoji kullanma (maksimum 2-3). Önemli noktaları vurgulamak için **kalın** metin kullan.

Bugün ${formattedDate} ve saat ${formattedTime}. Şimdi bu prensiplerle kullanıcıya harika bir deneyim yaşat!` :
      `${personalizedPrefix}You are GlowyAI, an intelligent and proactive marketing strategist. Your goal is not just to present data, but to turn it into a story, understand the user's objectives, and guide them to success. You are not a tool; you are a teammate dedicated to the user's success.

**Personality and Behavior Rules:**
1.  **Be Human and Insightful:** Try to understand the user's intent. Use confirmation phrases like, "Got it, so you're curious about the overall profitability of campaign X."
2.  **Be Proactive:** If you notice a significant anomaly, an unexpected success, or a risk in the data, bring it up without waiting for the user to ask. For example: "I've noticed the ROAS for this campaign has dropped by 40% in the last 3 days, which might be an issue. Would you like to investigate?"
3.  **Think Strategically:** Don't just report past data. Provide forward-looking strategic recommendations. "Based on this data, reallocating some budget from campaign X to campaign Y could be more profitable."
4.  **Be Simple and Clear:** Use plain language that anyone can understand, avoiding complex marketing jargon. Don't hesitate to use simple metaphors to visualize data.
5.  **Learn and Adapt:** Learn from the user's past conversations and preferences (this information will be provided to you).

**Technical Rules:**
*   **Data Fidelity:** NEVER generate imaginary data. If data is unavailable, offer alternatives like, "I can't access that specific information right now, but we could look at the data for X instead."
*   **Formatting:** Keep your answers concise. Use emojis sparingly (max 2-3). Use **bold** text to highlight key points.

Today is ${formattedDate}, and the time is ${formattedTime}. Now, go provide an amazing experience for the user based on these principles!`;

    // Hafıza özetini ana system prompt'a ekle (normal akış)
    systemContent = appendMemoryToPrompt(systemContent, userMemory);

    systemContent += `
🔧 GENERAL BEHAVIOR RULES (ALWAYS FOLLOW THESE):

1. COMMUNICATION STYLE:
   - Be friendly, professional, and encouraging
   - Use simple, clear language - avoid too much technical jargon
   - When explaining complex concepts, break them down into steps
   - Always be optimistic about improving performance

2. DATA ANALYSIS:
   - Never make assumptions about data you cannot see
   - Always ask for specific metrics, timeframes, or screenshots if unclear
   - Focus on actionable insights, not just reporting numbers
   - Prioritize ROI, ROAS, and conversion metrics in recommendations

3. RECOMMENDATIONS:
   - Provide specific, actionable next steps
   - Include reasoning behind each suggestion
   - Offer multiple options when possible (e.g., conservative vs aggressive strategies)
   - Always consider budget constraints in recommendations

4. LEARNING APPROACH:
   - Remember user preferences and past conversations
   - Adapt recommendations based on user's experience level
   - Ask clarifying questions to better understand goals
   - Personalize advice based on user's business type and size

5. SAFETY & ETHICS:
   - Never suggest violating platform policies
   - Recommend sustainable, long-term strategies
   - Warn about potential risks in aggressive strategies
   - Respect user privacy and data sensitivity

6. DIGITAL MARKETING PHILOSOPHY & APPROACH:
   - ROAS alone is not the only success metric - consider TOTAL REVENUE and VOLUME
   - Example: 300₺ spend with 10 ROAS = 3,000₺ revenue vs 5,000₺ spend with 5 ROAS = 25,000₺ revenue
   - Always prefer higher absolute profit over higher ROAS percentages when volume allows sustainable scaling
   - Focus on three key factors together: ROAS efficiency + Spend volume + Total revenue generated
   - Scale spending when ROAS is acceptable and total profit increases, even if efficiency slightly decreases
   - Look at the full funnel impact, not just immediate ROAS - consider customer lifetime value and repeat purchases

7. DAILY BUDGET OPTIMIZATION METHODOLOGY:
   - The biggest campaign optimization lever is typically BUDGET ALLOCATION (increasing or decreasing spend)
   - Use 7-day performance average as baseline to establish normal performance levels
   - Make budget decisions based on last 2 days ROAS performance compared to the 7-day average
   - If last 2 days ROAS > 7-day average: consider increasing budget
   - If last 2 days ROAS < 7-day average: consider decreasing budget or investigating issues
   - This approach balances data stability (7-day baseline) with responsiveness (2-day trend)
   - Always ensure sufficient data volume before making budget decisions - avoid knee-jerk reactions to single day performance

8. TARGET ROAS BIDDING OPTIMIZATION (AD GROUP LEVEL):
   - In search campaigns with Target ROAS bidding, each ad group may need different ROAS targets
   - Apply the same 7+2 day methodology for Target ROAS adjustments at ad group level
   - Calculate 7-day average ROAS per ad group as performance baseline
   - Compare last 2 days ROAS performance against the 7-day average for each ad group
   - If last 2 days ROAS > 7-day average: consider LOWERING Target ROAS to increase volume
   - If last 2 days ROAS < 7-day average: consider RAISING Target ROAS to improve efficiency
   - Note: Target ROAS adjustment direction is OPPOSITE to budget - lower target = more aggressive = more volume
   - Make incremental adjustments (10-20% changes) rather than dramatic shifts
   - Monitor for 2-3 days after Target ROAS changes before making further adjustments

9. BUDGET SCALING METHODOLOGY (SIGNIFICANT INCREASES):
   - When scaling budget significantly (e.g. 300K to 1M monthly), use strategic two-tier approach
   - TIER 1 - Campaign Type Analysis: First analyze performance by campaign types (Search, Display, Shopping, Video, etc.)
   - Identify best performing campaign types based on ROAS and revenue contribution
   - Allocate majority of additional budget to top performing campaign types
   - TIER 2 - Campaign Level Distribution: Within selected campaign types, distribute budget based on revenue generation
   - Prioritize campaigns with highest revenue contribution rather than just highest ROAS
   - Scale existing high-revenue campaigns before creating new ones
   - Example: If Search campaigns have best performance, increase budget of top revenue-generating Search campaigns first
   - Monitor scaling carefully - large budget increases can affect auction dynamics and performance

10. DAILY BUDGET REALLOCATION (SPEND CONTROL):
    - After setting monthly budget distribution (e.g. 1M monthly = ~33K daily), monitor actual daily spend vs planned
    - Check previous day's actual spend against allocated budget for each campaign daily
    - If campaigns underspent yesterday: Reallocate unused budget to higher-performing campaigns today
    - If campaigns overspent yesterday: Adjust today's allocation to stay on monthly target
    - Calculate cumulative month-to-date variance: (Actual spend - Planned spend) and adjust remaining days accordingly
    - Prioritize reallocation to campaigns with best recent performance (using 7+2 day methodology)
    - This prevents both underspending (losing opportunities) and overspending (exceeding monthly targets)
    - Example: If Campaign A allocated 5K but spent 3K, move 2K to Campaign B that's performing well and can absorb extra budget

11. CROSS-PLATFORM ANALYTICS TRACKING (CLICK vs SESSION DISCREPANCY):
    - Always compare ad platform click data with Google Analytics session data for accurate cost analysis
    - Click-to-session discrepancy is common: e.g. Google Ads reports 6,000 clicks but GA shows 3,500 sessions
    - Calculate real session rate: GA Sessions ÷ Platform Clicks (e.g. 3,500 ÷ 6,000 = 58% session rate)
    - Calculate actual cost per session: Total Spend ÷ GA Sessions (more accurate than platform CPC)
    - Use GA session data for ROAS calculations, not just platform conversion data
    - Monitor session rate trends - declining rates may indicate tracking issues or traffic quality problems
    - Factor session rate into campaign optimization: high click, low session campaigns need investigation
    - Example: If Campaign A has 90% session rate and Campaign B has 50%, prioritize budget to Campaign A
    - Always cross-reference conversion data between ad platforms and GA for data integrity`;
    
    // Kullanıcı hafızası varsa ekle
    if (userMemory) {
      systemContent += `\n\nIMPORTANT USER INFORMATION (Use this to personalize your responses):\n${JSON.stringify(userMemory, null, 2)}`;
      console.log('🧠✨ Using saved memory in conversation:', userMemory);
    } else {
      console.log('🆕 No saved memory found - starting fresh conversation');
    }

    formattedMessages.unshift({
      role: 'system',
      content: systemContent
    });

    const content = await callGlowyAI(formattedMessages, { narrative: true });

    // Kullanıcı ile yapılan sohbetten öğren (bunu arka planda yap)
    // İlk kullanıcı mesajından itibaren öğrenmeye başla
    if (messages.length >= 2) { // En az 1 user + 1 ai mesaj varsa öğren
      console.log('📖 Starting to learn from conversation with', messages.length, 'messages...');
      setTimeout(() => {
        learnFromConversation(messages, userId)
          .then(success => {
            if (success) console.log('✅ Successfully learned from conversation');
          });
      }, 0);
    }

    return content;
  } catch (error) {
    console.error('OpenAI API Error:', error);
    throw new Error('Failed to get response from AI. Please try again.');
  }
};

// Hafıza ve öğrenme fonksiyonları
const learnFromConversation = async (messages, userId) => {
  try {
    console.log('🎓 Starting to learn from conversation with', messages.length, 'messages');
    
    // Brand ID'yi al
    const brandId = localStorage.getItem('selectedBrandId') || 
                   localStorage.getItem('currentBrandId') ||
                   sessionStorage.getItem('selectedBrandId') ||
                   'f7e99143-5e9c-4bc8-9052-632613745bb2';
    
    // Memory endpoint not available; start fresh
    let existingMemory = {};
    
    // Yeni bilgileri çıkarmak için AI'ya sor
    const learningPrompt = [
      {
        role: 'system',
        content: `You are an AI learning system. Analyze the following conversation and extract key information about the user, their preferences, business details, and any other important information that should be remembered for future conversations.

IMPORTANT: You will be merging this with existing knowledge. Only extract NEW or UPDATED information from this conversation.

Format your response as JSON with relevant categories. Be comprehensive but avoid duplicating information that might already exist.

Example structure:
{
  "user_details": {
    "name": "...",
    "age": "...",
    "location": "..."
  },
  "business_details": {
    "company_name": "...",
    "industry": "...",
    "products": [...]
  },
  "preferences": {
    "goals": "...",
    "focus_areas": [...],
    "interests": [...]
  },
  "conversation_context": {
    "last_topic": "...",
    "recent_questions": [...]
  }
}`
      },
      ...messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content
      }))
    ];

    const learningContent = await callGlowyAI(learningPrompt, { narrative: false });
    const newLearningData = learningContent ? JSON.parse(learningContent) : {};
    console.log('🧠 New learning data from conversation:', newLearningData);
    
    // Akıllı merge işlemi: Eski bilgileri koru, yeni bilgileri ekle
    const mergedMemory = deepMergeMemory(existingMemory, newLearningData);
    console.log('🔄 Merged memory (old + new):', mergedMemory);
    
    console.log('💾 Saving merged memory with brand ID:', brandId);
    
    console.log('ℹ️ Skipping memory save (endpoint unavailable).');
    return true;
  } catch (error) {
    console.error('❌ Error saving memory:', error);
    return false;
  }
};
// Hafıza birleştirme fonksiyonu
const deepMergeMemory = (existingMemory, newMemory) => {
  // Eğer mevcut hafıza yoksa, yeni hafızayı döndür
  if (!existingMemory || Object.keys(existingMemory).length === 0) {
    return newMemory;
  }
  
  // Eğer yeni hafıza yoksa, mevcut hafızayı döndür
  if (!newMemory || Object.keys(newMemory).length === 0) {
    return existingMemory;
  }
  
  const merged = JSON.parse(JSON.stringify(existingMemory)); // Deep copy
  
  // Recursive merge
  const mergeObjects = (target, source) => {
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        // Object: recursive merge
        if (!target[key] || typeof target[key] !== 'object') {
          target[key] = {};
        }
        mergeObjects(target[key], source[key]);
      } else if (Array.isArray(source[key])) {
        // Array: combine and deduplicate
        if (!target[key]) {
          target[key] = [];
        } else if (!Array.isArray(target[key])) {
          target[key] = [target[key]];
        }
        
        // Combine arrays and remove duplicates
        const combined = [...target[key], ...source[key]];
        target[key] = [...new Set(combined)];
      } else if (source[key] !== null && source[key] !== undefined && source[key] !== '') {
        // Primitive: update if new value is not empty
        target[key] = source[key];
      }
    }
  };
  
  mergeObjects(merged, newMemory);
  return merged;
};

// Kullanıcı verilerini alıp sistem mesajına ekleyen fonksiyon
const getUserMemory = async (userId) => {
  try {
    // Brand ID'yi localStorage'dan al veya context'ten al
    const brandId = localStorage.getItem('selectedBrandId') || 
                   localStorage.getItem('currentBrandId') ||
                   sessionStorage.getItem('selectedBrandId') ||
                   'f7e99143-5e9c-4bc8-9052-632613745bb2'; // Fallback to observed brand ID
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log('ℹ️ No existing memory found for this user - this is normal for new users');
    } else {
      console.error('❌ Error fetching user memory:', error);
    }
    return null;
  }
};

// ===== Memory helpers for personalization and prompt context =====
const summarizeMemoryForPrompt = (memory) => {
  if (!memory || typeof memory !== 'object') return null;
  const userDetails = memory.user_details || memory.user || memory.profile || null;
  const preferences = memory.preferences || null;
  const business = memory.business_details || memory.business || null;
  const context = memory.conversation_context || null;
  const summary = {};
  if (userDetails) summary.user_details = userDetails;
  if (preferences) summary.preferences = preferences;
  if (business) summary.business_details = business;
  if (context) summary.conversation_context = context;
  return Object.keys(summary).length > 0 ? summary : null;
};

const appendMemoryToPrompt = (basePrompt, memory) => {
  const mem = summarizeMemoryForPrompt(memory);
  if (!mem) return basePrompt;
  return `${basePrompt}\n\nUSER CONTEXT (from memory):\n\n${JSON.stringify(mem, null, 2)}`;
};

const extractUserName = (memory) => {
  if (!memory || typeof memory !== 'object') return null;
  const details = memory.user_details || memory.user || memory.profile || {};
  const name = details.name || details.full_name || [details.first_name, details.last_name].filter(Boolean).join(' ').trim();
  return name || null;
};

const buildPersonalizedPrefix = (name, userLanguage = 'tr') => {
  if (!name) return '';
  return userLanguage === 'tr' ? `Tabii ki yardımcı olayım, ${name}. ` : `Of course, happy to help, ${name}. `;
};

export default function AIChat() {
  const [messages, setMessages] = useState(() => {
    // Güncel tarih ve saati al
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
    
    return [{
      id: 1,
      content: `Hello! I'm GlowyAI, your intelligent marketing assistant. Today is ${formattedDate}. 

🔄 **Loading your latest performance data...**

I can help you analyze your marketing data, provide insights, and offer optimization suggestions. How can I assist you today?`,
      sender: 'ai',
      isInitialMessage: true
    }];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastSuccessfulIntent, setLastSuccessfulIntent] = useState(null); // Add state for conversation context
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [suggestions] = useState([
    {
      id: 1,
      title: "Increase Google Ads Budget",
      description: "AI suggests increasing budget for high-performing campaigns",
      status: "pending", // pending, applied, rejected
      timestamp: "2024-02-20T10:00:00"
    },
    {
      id: 2,
      title: "Optimize Meta Ads Targeting",
      description: "Refine audience targeting based on recent performance data",
      status: "applied",
      timestamp: "2024-02-19T15:30:00"
    }
  ]);

  // Kullanıcı kimliği için state ekleyelim
  const [userId] = useState(() => {
    // Yerel depolamadan kullanıcı kimliğini al veya yeni oluştur
    const storedUserId = localStorage.getItem('glowyAiUserId');
    const finalUserId = storedUserId || uuidv4();
    console.log('🔑 Current User ID:', finalUserId);
    return finalUserId;
  });
  
  // Kullanıcı kimliğini kaydet
  useEffect(() => {
    if (!localStorage.getItem('glowyAiUserId')) {
      localStorage.setItem('glowyAiUserId', userId);
      console.log('💾 Saved new User ID to localStorage:', userId);
    } else {
      console.log('✅ Using existing User ID from localStorage:', userId);
    }
  }, [userId]);

  // İlk data loading effect'i
  useEffect(() => {
    const loadInitialData = async () => {
      if (hasLoadedInitialData) return;
      
      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-US', {
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
      
      const welcomeMessage = `Hey! I'm **GlowyAI**. I analyze your marketing data and give you actionable insights.

**What I can do:**
• Analyze Google Ads & Meta Ads performance
• Compare campaigns and find your best performers  
• Identify optimization opportunities
• Track trends and ROI across channels

**Just ask me things like:**
"Show me last month's performance" or "Which campaigns should I optimize?"

What would you like to know?`;

      // İlk mesajı güncelle
      setMessages(prev => prev.map(msg => 
        msg.isInitialMessage ? {
          ...msg,
          content: welcomeMessage,
          isInitialMessage: false
        } : msg
      ));
      
      setHasLoadedInitialData(true);
    };
    
    // Component mount olduktan 1 saniye sonra data çek
    const timer = setTimeout(loadInitialData, 1000);
    return () => clearTimeout(timer);
  }, [hasLoadedInitialData]);

  useEffect(() => {
    if (messagesEndRef.current) {
      const chatContainer = messagesEndRef.current.closest('.flex-1.overflow-y-auto');
      if (chatContainer) {
        setTimeout(() => {
          chatContainer.scrollTop = chatContainer.scrollHeight;
          window.scrollTo(window.scrollX, window.scrollY);
        }, 0);
      }
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      content: input.trim(),
      sender: 'user'
    };

    // Kullanıcı mesajını ekle
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Yükleniyor mesajı ekle
      const loadingMsgId = Date.now() + 1;
      setMessages(prev => [...prev, {
        id: loadingMsgId,
        content: "Thinking...",
        sender: 'ai',
        isLoading: true
      }]);

      // OpenAI API'ye istek gönder (userId ekleyerek)
      const apiMessages = [...messages, userMessage]; 
      const aiResponse = await sendMessageToOpenAI(apiMessages, userId);

      // Yükleniyor mesajını kaldır
      setMessages(prev => prev.filter(msg => msg.id !== loadingMsgId));

      // AI yanıtını ekle
      setMessages(prev => [...prev, {
        id: Date.now() + 2,
        content: aiResponse,
        sender: 'ai'
      }]);

      // Analiz ederek öneriler çıkarabilir ve suggestions state'i güncellenebilir
      // Bu kısmı daha sonra geliştirebiliriz
    } catch (error) {
      console.error('Error:', error);
      
      // Hata mesajını ekle
      setMessages(prev => [...prev.filter(msg => !msg.isLoading), {
        id: Date.now() + 3,
        content: "Sorry, I encountered an error. Please try again.",
        sender: 'ai',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Önceden tanımlanmış hızlı sorular
  const handleQuickQuestion = (question) => {
    setInput(question);
  };

  // 💰 BUDGET OPTIMIZATION - Simplified version
  const handleBudgetOptimization = async () => {
    try {
      setIsTriggering(true);
      
      const brandId = localStorage.getItem('selectedBrandId') || 
                     localStorage.getItem('currentBrandId') ||
                     sessionStorage.getItem('selectedBrandId');
      
      if (!brandId) {
        throw new Error('Brand ID not found in localStorage');
      }

      console.log('💰 BUDGET OPTIMIZATION: Creating budget optimization task...');

      // Call the static suggestion API
      const response = await axios.post(
        'https://api.glowytics.com/api/ai-suggestions/generate-static',
        {
          brandId: brandId
        },
        {
          headers: {
            'X-Brand-ID': brandId, 
            'Content-Type': 'application/json'
          },
          withCredentials: true,
          timeout: 10000
        }
      );

      console.log('✅ Budget optimization task created:', response.data);

      if (response.data.success) {
        setMessages(prev => [...prev, {
          id: Date.now(),
          content: `💰 **Budget Optimization Task Created!** \n\n✅ ${response.data.message}\n\n**📊 Task Details:**\n• **${response.data.suggestion.title}**\n• Category: Budget Optimization\n• Priority: ${response.data.suggestion.priority.toUpperCase()}\n• Status: ${response.data.suggestion.status}\n• Created: ${new Date(response.data.suggestion.created_at).toLocaleString('en-US')}\n\n💡 **You can view this task in the Task Board under "GlowyAI Suggestions" column!**\n\n🎯 **Next Steps:**\n1. Go to Task Board page\n2. Find your task in "GlowyAI Suggestions" column\n3. Click "Apply" button to accept the task\n4. Perform Google Ads budget analysis\n\n**This budget optimization task helps you analyze and optimize your campaign budgets! 🚀**`,
          sender: 'ai'
        }]);
      } else {
        throw new Error(response.data.error || 'Failed to create budget optimization task');
      }
    } catch (error) {
      console.error('❌ Budget Optimization Error:', error);
      
      let errorMessage = 'Budget optimization task could not be created';
      let errorDetails = '';
      
      if (error.response) {
        console.error('❌ Error response data:', error.response.data);
        console.error('❌ Error response status:', error.response.status);
        errorMessage = error.response.data?.error || error.response.data?.message || error.message;
        errorDetails = `\n\n**Technical Details:**\n• Status: ${error.response.status}\n• Error: ${error.response.data?.details || 'N/A'}`;
      } else if (error.request) {
        errorMessage = 'API connection failed';
        errorDetails = '\n\n**Problem:** Cannot reach backend';
      } else {
        errorMessage = error.message;
      }

      setMessages(prev => [...prev, {
        id: Date.now(),
        content: `❌ **Budget Optimization Error** \n\n${errorMessage}${errorDetails}\n\n🔧 **Solutions:**\n• Refresh browser and try again\n• Check your network connection\n• Verify brand selection\n\n💡 Try asking "budget optimization" in the chat as an alternative.`,
        sender: 'ai',
        isError: true
      }]);
    } finally {
      setIsTriggering(false);
    }
  };
  const [isTriggering, setIsTriggering] = useState(false);

  return (
    <div className="relative">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Chat Section */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:shadow-[0_15px_35px_rgba(0,0,0,0.15)] transition-all duration-300 border border-gray-100 flex flex-col h-[calc(100vh-8rem)]">
          {/* Chat Header */}
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-gray-800">GlowyAI</h2>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded-full">
                    <img src="/openai.png" alt="OpenAI Logo" className="w-4 h-4" />
                    <span className="text-xs font-medium text-blue-600">Powered by OpenAI</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Your intelligent marketing companion</p>
                <p className="text-xs text-gray-400 font-mono">User ID: {userId.substring(0, 8)}...</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <Download className="w-4 h-4" />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <Activity className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map(message => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.sender === 'ai' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 mr-2">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className={`
                  max-w-[80%] rounded-2xl px-4 py-3 shadow-sm
                  ${message.sender === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : message.isError 
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-gray-100 text-gray-800'}
                  ${message.isLoading ? 'animate-pulse' : ''}
                `}>
                  <div 
                    className=""
                    dangerouslySetInnerHTML={{ __html: processMessageContent(message.content) }}
                  />
                  {message.sender === 'ai' && !message.isLoading && (
                    <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <img src="/openai.png" alt="OpenAI" className="h-3" />
                        <span className="text-xs text-gray-500">OpenAI</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                          <Star className="w-3 h-3" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                          <AlertTriangle className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100">
            <div className="relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                placeholder="Ask anything about your marketing data..."
                className="w-full px-4 py-3 pr-24 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-hidden min-h-[48px] max-h-[200px]"
                style={{
                  height: input ? `${Math.min(200, Math.max(48, input.split('\n').length * 24 + 24))}px` : '48px'
                }}
                disabled={isLoading}
                rows={1}
              />
              <div className="absolute right-14 bottom-3 flex items-center gap-2">
                <div className="text-xs text-gray-400 border-r border-gray-200 pr-2">Powered by</div>
                <img src="/openai.png" alt="OpenAI" className="h-5" />
              </div>
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className={`absolute right-2 bottom-2 p-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg transition-all ${
                  isLoading || !input.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button 
                type="button"
                onClick={() => handleQuickQuestion("Analyze our Google Ads performance for the last month")}
                className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Analyze Google Ads
              </button>
              <button 
                type="button"
                onClick={() => handleQuickQuestion("Suggest optimizations for our current campaigns")}
                className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Optimize campaigns
              </button>
              <button 
                type="button"
                onClick={() => handleQuickQuestion("Explain what ROAS and CPA metrics mean")}
                className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Explain metrics
              </button>
              <button 
                type="button"
                onClick={() => handleQuickQuestion("Compare our Facebook and Instagram ad performance")}
                className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Compare platforms
              </button>
            </div>
          </form>
        </div>

        {/* Suggestions Section */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:shadow-[0_15px_35px_rgba(0,0,0,0.15)] transition-all duration-300 border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-800">AI Suggestions</h2>
                <p className="text-xs text-gray-500">Smart recommendations for your campaigns</p>
              </div>
            </div>
            <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded-full">
              <img src="/openai.png" alt="OpenAI Logo" className="w-4 h-4" />
              <span className="text-xs font-medium text-blue-600">Powered by OpenAI</span>
            </div>
          </div>

          <div className="space-y-4">
            {suggestions.map(suggestion => (
              <div key={suggestion.id} className="p-4 rounded-xl border border-gray-100 hover:border-blue-100 transition-colors shadow-sm hover:shadow-md">
                <div className="flex items-start justify-between">
                  <h3 className="font-medium text-gray-800">{suggestion.title}</h3>
                  {suggestion.status === 'applied' ? (
                    <div className="p-1 bg-green-100 rounded-full">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    </div>
                  ) : (
                    <div className="p-1 bg-yellow-100 rounded-full">
                      <Clock className="w-4 h-4 text-yellow-500" />
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">{suggestion.description}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {new Date(suggestion.timestamp).toLocaleDateString()}
                  </span>
                  {suggestion.status === 'pending' && (
                    <button className="px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg hover:opacity-90 transition-opacity">
                      Apply Suggestion
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Budget Optimization Button */}
          <button
            onClick={handleBudgetOptimization}
            disabled={isTriggering}
            className={`w-full mt-4 p-3 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-blue-600 rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 ${
              isTriggering ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isTriggering ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <span>💰</span>
                <span>Budget Optimization</span>
              </>
            )}
          </button>
          
          <button className="w-full mt-4 p-3 text-sm font-medium text-blue-600 bg-blue-50 rounded-xl border border-blue-100 hover:bg-blue-100 transition-colors flex items-center justify-center gap-2">
            <span>View All Suggestions</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
} 

